/**
 * Free Gift Manager — Layer 1 (theme only)
 *
 * Shared core (single source of truth):
 *   getFreshCart, giftExistsInCart, calculateMainProductsTotal, evaluateTierAndSync
 *
 * Tier 1: subtotal threshold only (no code).
 * Tier 2: subtotal + valid theme-only code (persisted in session/local storage).
 * Never applies native Shopify discount codes for Tier 2 theme codes.
 */

(function () {
  'use strict';

  const log = (...args) => console.log('%c[FreeGift]', 'color:#0a0; font-weight:bold', ...args);
  const TIER2_CODE_STORAGE = 'ck_tier2_gift_code';
  const TIER2_PENDING_STORAGE = 'ck_tier2_pending_gift_code';

  // Expedited-shipping variant IDs (same product as express-checkout-config). Local set only —
  // do not call multi-ship helpers (that coupling broke Tier 1/Tier 2 gift sync).
  const KNOWN_SHIPPING_VARIANT_IDS = new Set([
    '44113130487878',
    '44113130520646',
    '44113130553414',
    '44113130586182',
    '44509266509894',
    '44509266542662',
    '44509266575430',
    '44509266608198',
  ]);

  function isKnownShippingVariantId(variantId) {
    const idStr = String(variantId != null ? variantId : '').trim();
    if (!idStr) return false;
    if (KNOWN_SHIPPING_VARIANT_IDS.has(idStr)) return true;
    try {
      const cfg = window.expressCheckoutConfig || window.chukarExpressCheckoutConfig || {};
      const variants = Array.isArray(cfg.shippingVariants) ? cfg.shippingVariants : [];
      return variants.some((v) => {
        if (!v) return false;
        const vid = v.id != null ? v.id : v.variantId != null ? v.variantId : v.variant_id;
        return String(vid != null ? vid : '').trim() === idStr;
      });
    } catch (_) {
      return false;
    }
  }

  /* ─── Property helpers ─── */

  function getProp(item, key) {
    if (!item || !item.properties) return undefined;
    const p = item.properties;
    if (Array.isArray(p)) {
      const pair = p.find((pr) => Array.isArray(pr) && String(pr[0]) === key);
      return pair ? pair[1] : undefined;
    }
    return p[key];
  }

  function isShippingMerchLine(item) {
    if (!item) return false;
    // Configured gift SKUs are never shipping — even if _shipping was wrongly copied on.
    try {
      if (isConfiguredTierGiftVariant(item, '1') || isConfiguredTierGiftVariant(item, '2')) {
        return false;
      }
    } catch (_) {}
    if (getProp(item, '_shipping') === 'true' || getProp(item, '_shipping') === true) return true;
    if (getProp(item, '_fee') === 'true' || getProp(item, '_fee') === true) return true;
    if (getProp(item, '_ribbon') === 'true' || getProp(item, '_ribbon') === true) return true;
    const vid = item.variant_id != null ? item.variant_id : item.id;
    if (isKnownShippingVariantId(vid)) return true;
    // Keep narrow — only the shipping SKU titles. Broad /\bshipping\b/ matched merchandise and
    // blocked Tier 2 gift detection / auto-add after discount code apply.
    const title = (item.title || item.product_title || item.variant_title || '').toLowerCase();
    return title.includes('shipping information') || title.includes('shipping info');
  }

  /**
   * Cart Transform bundle parent — cart/change on this key replaces the whole line and
   * wipes sibling shipping recipient props (_address, Card message, etc.).
   */
  function isCartTransformBundleParentLine(item) {
    if (!item) return false;
    if (item._isComponent === true) return false;
    if (item.has_components === true || item.has_components === 'true') return true;
    if (Array.isArray(item.item_components) && item.item_components.length > 0) return true;
    try {
      const key = item.key || item.line_key;
      if (!key) return false;
      const map = window.cartItemComponents;
      const bundle = map && map[String(key)];
      if (bundle && Array.isArray(bundle.components) && bundle.components.length > 0) {
        return true;
      }
    } catch (_) {}
    return false;
  }

  function isMainProductForGiftThreshold(item) {
    if (!item) return false;
    if (isGiftMarkedLine(item)) return false;
    if (itemIsTierGift(item, '1') || itemIsTierGift(item, '2')) return false;
    if (getProp(item, '_shipping') === 'true' || getProp(item, '_shipping') === true) return false;
    if (getProp(item, '_fee') === 'true' || getProp(item, '_fee') === true) return false;
    if (getProp(item, '_ribbon') === 'true' || getProp(item, '_ribbon') === true) return false;
    return getProp(item, '_product') === 'true' || getProp(item, '_product') === true;
  }

  function getGiftProp(item, ...keys) {
    for (let i = 0; i < keys.length; i++) {
      const v = getProp(item, keys[i]);
      if (v != null && String(v).trim() !== '') return v;
    }
    return undefined;
  }

  function isTier2GiftLine(item) {
    if (!item || isShippingMerchLine(item)) return false;

    const code = getGiftProp(item, '_Tier_2_Discount_code', '_free_gift_discount_code');
    if (code) {
      if (getProp(item, '_free_gift_included') === 'true' || getProp(item, '_free_gift_included') === true) {
        return true;
      }
      if (String(getProp(item, '_free_gift_tier') || '') === '2') return true;
      if (getGiftProp(item, '_Free_gift_price', '_free_gift_price')) return true;
      if (getProp(item, '_free_gift_promo') === 'true' || getProp(item, '_free_gift_promo') === true) {
        return true;
      }
      // Tier 2 discount code alone is enough — Layer 3 / Cart Transform may drop companion props.
      return true;
    }

    // Cart Transform often drops _Tier_2_Discount_code from nested components.
    // Still treat as Tier 2 when gift markers + configured Tier 2 SKU are present.
    if (String(getProp(item, '_free_gift_tier') || '') === '2') return true;

    const included =
      getProp(item, '_free_gift_included') === 'true' ||
      getProp(item, '_free_gift_included') === true;
    const promo =
      getProp(item, '_free_gift_promo') === 'true' ||
      getProp(item, '_free_gift_promo') === true;
    if ((included || promo) && isConfiguredTierGiftVariant(item, '2')) return true;

    // After Save / page refresh, Cart Transform can strip every gift identity prop from the
    // merchandise component while keeping `_gid: gift_…`. That still uniquely identifies the
    // Tier 2 promo gift. Paid purchases of the same SKU use normal (non-gift_) GIDs, so they
    // stay editable and are never treated as gifts.
    if (isConfiguredTierGiftVariant(item, '2')) {
      const gid = String(getProp(item, '_gid') || '');
      if (gid.startsWith('gift_')) return true;
      // Assign-to-existing-bundle replaces gift_ with the paid bundle GID and may strip props.
      // Multiship stash is scoped to that GID + variant — never marks a paid same-SKU line.
      if (isAssignedTier2GiftFromMultishipStash(item)) return true;
    }

    return false;
  }

  function isGiftMarkedLine(item) {
    if (!item || isShippingMerchLine(item)) return false;
    if (getProp(item, '_free_gift_included') === 'true' || getProp(item, '_free_gift_included') === true) {
      return true;
    }
    if (isTier2GiftLine(item)) return true;
    if (getProp(item, '_free_gift') === 'true' || getProp(item, '_free_gift') === true) {
      return true;
    }
    if (getProp(item, '_free_gift_promo') === 'true' || getProp(item, '_free_gift_promo') === true) {
      return true;
    }
    return false;
  }

  function lineMatchesGiftTier(item, tier) {
    if (!isGiftMarkedLine(item)) return false;
    const t = String(tier);
    const newTier = getProp(item, '_free_gift_tier');
    if (newTier != null && String(newTier) === t) return true;
    if (t === '1') {
      const promo = getProp(item, '_free_gift_promo');
      if (promo === 'true' || promo === true) return false;
      return getProp(item, '_free_gift') === 'true' || getProp(item, '_free_gift') === true;
    }
    if (t === '2') {
      if (getProp(item, '_free_gift_tier') === '2') return true;
      if (isTier2GiftLine(item)) return true;
      return (
        getProp(item, '_free_gift_promo') === 'true' ||
        getProp(item, '_free_gift_promo') === true
      );
    }
    return false;
  }

  function getTierVariantIds(tier) {
    const s = getSettings();
    const ids = new Set();
    const t = String(tier);
    if (t === '1') {
      if (s.variantId) ids.add(String(s.variantId));
      (Array.isArray(s.variantIds) ? s.variantIds : []).forEach((v) => ids.add(String(v)));
    } else if (t === '2') {
      const pg = s.promoGift;
      if (pg) {
        if (pg.variantId) ids.add(String(pg.variantId));
        (Array.isArray(pg.variantIds) ? pg.variantIds : []).forEach((v) => ids.add(String(v)));
      }
    }
    return ids;
  }

  function isConfiguredTierGiftVariant(item, tier) {
    const vid =
      item && item.variant_id != null
        ? String(item.variant_id)
        : item && item.id != null
          ? String(item.id)
          : '';
    if (!vid) return false;
    return getTierVariantIds(tier).has(vid);
  }

  function isZeroPriceLine(item) {
    if (!item) return false;
    const unit = Number(item.price);
    const line = Number(
      item.final_line_price != null
        ? item.final_line_price
        : item.line_price != null
          ? item.line_price
          : item.final_price != null
            ? item.final_price
            : unit
    );
    return unit === 0 || line === 0;
  }

  /**
   * True when this line is the tier gift — by properties OR configured gift variant at $0
   * inside a bundle (Cart Transform often drops _free_gift from component properties).
   * Never treat a Tier 2 gift as Tier 1 (or vice versa) — shared SKU / gift_ GID must not
   * cause Tier 1 removal to delete an unrelated Tier 2 bundle when upgrading.
   */
  function itemIsTierGift(item, tier) {
    if (!item || isShippingMerchLine(item)) return false;
    const tierStr = String(tier);

    if (tierStr === '2') {
      return isTier2GiftLine(item);
    }

    // Tier 1 only — explicit exclusion of Tier 2 identity (property-based, never variant-only).
    if (isTier2GiftLine(item)) return false;
    if (String(getProp(item, '_free_gift_tier') || '') === '2') return false;
    if (getGiftProp(item, '_Tier_2_Discount_code', '_free_gift_discount_code')) return false;

    if (lineMatchesGiftTier(item, '1')) return true;

    if (!isConfiguredTierGiftVariant(item, '1')) return false;

    const gid = String(getProp(item, '_gid') || '');
    if (gid.startsWith('gift_')) return true;

    if (isGiftMarkedLine(item)) return lineMatchesGiftTier(item, '1');

    if (isZeroPriceLine(item)) return true;

    return false;
  }

  function isAnyTierGiftLine(item) {
    return itemIsTierGift(item, '1') || itemIsTierGift(item, '2');
  }

  function mainProductLineCents(item) {
    if (!item) return 0;
    const qty = Number(item.quantity) || 1;
    const unitCents = Number(item.price);
    if (!isNaN(unitCents) && unitCents > 0) {
      return Math.round(unitCents * qty);
    }
    const fe = getProp(item, '_main_fees');
    if (fe != null && fe !== '') {
      const dollarsPerUnit = parseFloat(String(fe).replace(/[^0-9.-]/g, ''));
      if (!isNaN(dollarsPerUnit) && dollarsPerUnit > 0) {
        return Math.round(dollarsPerUnit * 100 * qty);
      }
    }
    if (item.original_line_price != null) {
      const o = Number(item.original_line_price);
      if (!isNaN(o) && o > 0) return Math.round(o);
    }
    if (item.final_line_price != null) return Number(item.final_line_price) || 0;
    return 0;
  }

  function getDOMTotalCents() {
    /* Prefer last-in-bundle Gift Summary on the shipping page (same as footer sync).
       Ignore modal copies — after Save & Continue they can undercount multi-ship totals. */
    const root = document.querySelector('shipping-page') || document;
    let summaries = root.querySelectorAll('multiship-product.is_last_in_bundle .gift_summary');
    if (!summaries.length) {
      summaries = root.querySelectorAll('multiship-product .gift_summary');
    }
    if (!summaries.length) {
      summaries = root.querySelectorAll('.gift_summary');
    }
    const seenGids = new Set();
    let total = 0;
    for (const gs of summaries) {
      if (gs.closest && gs.closest('modal-dialog')) continue;
      const row = gs.closest('multiship-product');
      const gid = row ? row.dataset.gid : null;
      if (!gid || seenGids.has(gid)) continue;
      seenGids.add(gid);
      const dts = gs.querySelectorAll('dt');
      for (const dt of dts) {
        if (dt.textContent.trim() === 'Product Total:') {
          const dd = dt.nextElementSibling;
          if (dd && dd.tagName === 'DD') {
            const text = dd.textContent.replace(/[^0-9.]/g, '');
            const value = parseFloat(text);
            if (!isNaN(value)) total += Math.round(value * 100);
          }
          break;
        }
      }
    }
    return seenGids.size > 0 ? total : null;
  }

  /**
   * During FreeGift→Bundle address assign / section swap, gift_summary rows can exist with
   * Product Total still $0.00 while cart.js still has full merchandise. Treating that $0 as
   * authoritative demotes CHERRYJAM and removes the gift, then re-adds it seconds later.
   *
   * Also: after Save & Continue on one multi-ship recipient, DOM may only paint ONE bundle's
   * Product Total (e.g. $63.89) while cart merchandise is still $125+ across shipments.
   * That incomplete DOM must not demote Tier 2 / remove the gift.
   */
  function cartMerchandiseHintCents(freshCart) {
    let hint = 0;
    try {
      hint = Math.max(hint, cartItemComponentsMainProductTotalCents() || 0);
    } catch (_) {}
    try {
      if (freshCart && Array.isArray(freshCart.items)) {
        // Merchandise-only sources only. Opaque CT parents without item_components used to
        // inflate calculateMainProductsFinalTotal (merch + shipping) and mark Gift Summary
        // "$124.75" unreliable vs a $150+ hint — keeping the free gift under threshold.
        hint = Math.max(
          hint,
          cartMainProductTotalCents(freshCart.items) || 0,
          calculateMainProductsTotal(freshCart) || 0
        );
      }
    } catch (_) {}
    return hint;
  }

  /** Merchandise-only hint — never includes opaque-parent shipping inflation. */
  function cartMerchandiseOnlyHintCents(freshCart) {
    let hint = 0;
    try {
      hint = Math.max(hint, cartItemComponentsMainProductTotalCents() || 0);
    } catch (_) {}
    try {
      if (freshCart && Array.isArray(freshCart.items)) {
        hint = Math.max(hint, cartMainProductTotalCents(freshCart.items) || 0);
      }
    } catch (_) {}
    return hint;
  }

  function countShippingPagePaidProductGids() {
    const gids = new Set();
    try {
      document.querySelectorAll('shipping-page multiship-product[data-gid]').forEach((row) => {
        const gid = row && row.dataset ? row.dataset.gid : '';
        if (!gid || String(gid).startsWith('gift_')) return;
        gids.add(String(gid));
      });
    } catch (_) {}
    return gids.size;
  }

  /**
   * Visible free-gift row on the shipping page (assigned into a paid bundle or standalone).
   * Used when cart.js / giftIdentityStillInLiveCart miss a key-rotated gift still on screen.
   */
  function shippingPageHasVisibleFreeGiftRow(tier) {
    const tierStr = tier != null && String(tier) !== '' ? String(tier) : '';
    try {
      const rows = document.querySelectorAll(
        'shipping-page multiship-product[data-free-gift-line="true"], ' +
          'shipping-page multiship-product.multiship-product--free-gift'
      );
      for (const row of rows) {
        if (!row) continue;
        const vid =
          row.getAttribute('data-variant-id') ||
          (row.querySelector('[data-quantity-variant-id]') &&
            row.querySelector('[data-quantity-variant-id]').getAttribute('data-quantity-variant-id')) ||
          '';
        if (!tierStr) return true;
        if (vid && isConfiguredTierGiftVariant({ variant_id: vid, id: vid }, tierStr)) {
          return true;
        }
        // Marker without a readable variant still means a gift row is painted.
        if (!vid && row.getAttribute('data-free-gift-line') === 'true') return true;
      }
    } catch (_) {}
    return false;
  }

  function isShippingDomMerchandiseReliable(domCents, freshCart) {
    if (domCents == null || isNaN(domCents)) return false;
    const merchOnly = cartMerchandiseOnlyHintCents(freshCart);
    const hint = Math.max(cartMerchandiseHintCents(freshCart), merchOnly);

    // Transient $0 while cart still has merchandise
    if (domCents <= 0) {
      if (hint >= 100) return false;
      return true;
    }

    // UNDER-THRESHOLD-GIFT-REMOVE-2026-09-15: Gift Summary under Tier 1 threshold that agrees
    // with merchandise-only sources (or those sources are also under) is authoritative — the
    // progress banner already shows "You're $X away". Do not let shipping-inflated cart.js
    // finals mark it unreliable and keep the free gift.
    try {
      const tier1Min = Math.round((parseFloat(getSettings().threshold) || 125) * 100);
      if (domCents > 0 && domCents < tier1Min) {
        if (
          merchOnly <= 0 ||
          merchOnly < tier1Min ||
          Math.abs(domCents - merchOnly) <= Math.max(Math.round(tier1Min * 0.15), 2500)
        ) {
          return true;
        }
      }
    } catch (_) {}

    // Incomplete multi-ship Gift Summary vs live cart (Save & Continue on one recipient).
    // Example: DOM $63.89 for one shipment while components/cart still show ~$127+.
    if (hint >= 100 && domCents < hint * 0.85) {
      return false;
    }

    // Multiple paid shipments on page but DOM only captured a subset of Product Totals.
    try {
      const paidGids = countShippingPagePaidProductGids();
      if (paidGids >= 2 && hint >= 100 && domCents < hint * 0.95) {
        return false;
      }
    } catch (_) {}

    return true;
  }

  function cartItemComponentsMainProductTotalCents() {
    const map = window.cartItemComponents;
    if (!map || typeof map !== 'object') return 0;
    let total = 0;
    for (const bundle of Object.values(map)) {
      if (!bundle || !Array.isArray(bundle.components)) continue;
      const bgid = bundle.gid != null ? String(bundle.gid) : '';
      if (bgid.startsWith('gift_')) continue;
      for (const comp of bundle.components) {
        if (!isMainProductForGiftThreshold(comp)) continue;
        total += mainProductLineCents(comp);
      }
    }
    return total;
  }

  function cartMainProductTotalCents(items) {
    let total = 0;
    for (const item of items || []) {
      if (!item) continue;
      if (isGiftMarkedLine(item) || itemIsTierGift(item, '1') || itemIsTierGift(item, '2')) continue;
      if (item.item_components && item.item_components.length) {
        for (const comp of item.item_components) {
          if (!isMainProductForGiftThreshold(comp)) continue;
          total += mainProductLineCents(comp);
        }
        continue;
      }
      if (!isMainProductForGiftThreshold(item)) continue;
      total += mainProductLineCents(item);
    }
    return total;
  }

  function resolveEffectiveMerchandiseTotalCents(items) {
    const domTotal = getDOMTotalCents();
    const cartTotal = cartMainProductTotalCents(items);
    const componentsTotal = cartItemComponentsMainProductTotalCents();
    const hasComponentsMap =
      typeof window.cartItemComponents === 'object' &&
      window.cartItemComponents !== null &&
      Object.keys(window.cartItemComponents).length > 0;

    // Multiship: `cartItemComponents` is the reliable per-bundle main list. cart.js can merge
    // shipping into line prices and inflate totals if we Math.max with it — that falsely
    // unlocked Tier 1 below the $125 merchandise threshold (regression from Tier 2 settle work).
    if (hasComponentsMap && componentsTotal > 0) {
      if (domTotal !== null && domTotal > 0 && domTotal >= componentsTotal * 0.99) {
        // DOM Product Total can briefly include shipment fallbacks; ignore large DOM overshoots.
        // EXCEPTION (Tier 2 code applied): automatic discounts (e.g. Buy 4 Save $5) lower
        // component `price` so componentsTotal can sit just under $135 while gift_summary
        // Product Total still shows pre-discount merchandise (≥ threshold). Preferring
        // discounted components here cleared BISCOTTI after assign-to-single-bundle.
        if (domTotal > componentsTotal * 1.12) {
          if (getAppliedTier2Code()) {
            const tier2Min = getTier2ThresholdCents();
            if (tier2Min != null && componentsTotal < tier2Min && domTotal >= tier2Min) {
              log(
                `Effective total (Tier 2: keep DOM Product Total $${(domTotal / 100).toFixed(2)} over discounted components $${(componentsTotal / 100).toFixed(2)})`
              );
              return domTotal;
            }
          }
          log(
            `Effective total prefers cartItemComponents ($${(componentsTotal / 100).toFixed(2)}) over inflated DOM ($${(domTotal / 100).toFixed(2)})`
          );
          return componentsTotal;
        }
        const effective = Math.max(componentsTotal, domTotal);
        log(
          `Effective total (main products, cartItemComponents + DOM): $${(effective / 100).toFixed(2)}`
        );
        return effective;
      }

      // Tier 2 code + CT settle only: allow a modest lift when components undercounts. Never
      // unbounded Math.max with cartTotal (shipping inflation → false Tier 1/2 eligibility).
      // Also lift DOM when it alone keeps Tier 2 above threshold (Buy 4 discount undercount).
      if (getAppliedTier2Code()) {
        const liftCandidates = [componentsTotal];
        if (cartTotal > 0 && cartTotal <= componentsTotal * 1.2) {
          liftCandidates.push(cartTotal);
        }
        if (domTotal !== null && domTotal > 0 && domTotal <= componentsTotal * 1.2) {
          liftCandidates.push(domTotal);
        }
        const tier2Min = getTier2ThresholdCents();
        if (
          tier2Min != null &&
          componentsTotal < tier2Min &&
          domTotal !== null &&
          domTotal >= tier2Min &&
          domTotal <= componentsTotal * 1.35
        ) {
          // Cap at 35% above components so shipping-inflated DOM cannot unlock falsely.
          liftCandidates.push(domTotal);
        }
        const lifted = Math.max(...liftCandidates);
        if (lifted !== componentsTotal) {
          log(
            `Effective total (Tier 2 modest lift): $${(lifted / 100).toFixed(2)} (components $${(componentsTotal / 100).toFixed(2)})`
          );
        } else {
          log(
            `Effective total (main products, cartItemComponents unit price × qty): $${(componentsTotal / 100).toFixed(2)}`
          );
        }
        return lifted;
      }

      log(
        `Effective total (main products, cartItemComponents unit price × qty): $${(componentsTotal / 100).toFixed(2)}`
      );
      return componentsTotal;
    }

    const candidates = [cartTotal, componentsTotal];
    if (domTotal !== null) candidates.push(domTotal);
    const effectiveTotal = Math.max(...candidates, 0);
    if (domTotal !== null && domTotal < effectiveTotal) {
      log(
        `Main-product total uses cart/components ($${(effectiveTotal / 100).toFixed(2)}) instead of partial gift_summary DOM ($${(domTotal / 100).toFixed(2)})`
      );
    } else {
      log(`Effective total (fallback): $${(effectiveTotal / 100).toFixed(2)}`);
    }
    return effectiveTotal;
  }

  /**
   * True while multi-ship Save / rebuild is still mutating the cart — free-gift must not
   * remove Tier 2 gifts yet.
   *
   * Do NOT treat shipping-page `_suppressCartTierScheduleUntil` as settling: that flag only
   * blocks multi-ship's own scheduleTierUpdate (7–12s) and left free-gift deferring with the
   * shipping-page loader stuck / `__freeGiftSyncPending` blocking hideShippingPageLoader.
   */
  function isMultishipCartSettling() {
    try {
      if (window.__multishipCartMutating || window.__multishipApplyAllInProgress) return true;
      // Ship-to-Single background `_gid` merge (GraphQL / batch / property) — do not
      // race free-gift add/remove while the cart is being rewritten.
      if (
        window.__multishipShipToSingleBgMergeInFlight ||
        window.__multishipShipToSingleBgMergeQueued ||
        window.__multishipShipToSingleRestoreInFlight
      ) {
        return true;
      }
      // While Ship-to-Single recipient modal is open (awaiting Save), do not race
      // free-gift remove/re-add — gift is preserved by multi-ship merge.
      if (
        window.__multishipShipToSinglePending &&
        !window.__multishipShipToSingleSaveInProgress
      ) {
        return true;
      }
      // After Ship-to-Single merge, keep free-gift quiet so CT/hydration lag cannot
      // trigger a false "gift missing → remove/re-add" cycle.
      if (
        window.__multishipShipToSingleGiftQuietUntil &&
        Date.now() < window.__multishipShipToSingleGiftQuietUntil
      ) {
        return true;
      }
      if (
        window.__multishipSaveContinueSettlingUntil &&
        Date.now() < window.__multishipSaveContinueSettlingUntil
      ) {
        return true;
      }
    } catch (_) {}
    return false;
  }

  function isMultiPackPricePreviewActive() {
    try {
      return window.__multiPackCartPricePreview === true;
    } catch (_) {
      return false;
    }
  }

  async function refreshCartItemComponents() {
    try {
      const res = await fetch('/?sections=cart-components-data', {
        credentials: 'same-origin',
        cache: 'no-store',
      });
      if (!res.ok) return;
      const data = await res.json();
      const html = data['cart-components-data'];
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const scriptContent = doc.querySelector('#cart-components-data')?.textContent;
      if (scriptContent) {
        new Function(scriptContent)();
        log('cartItemComponents refreshed');
      }
    } catch (_) {}
  }

  function parseTier2Codes(settings) {
    const codes = new Set();
    const pg = settings && settings.promoGift;
    if (!pg || !pg.enabled) return codes;
    const raw = (pg.code || '').trim();
    if (!raw) return codes;
    raw.split(/[\n,]+/).forEach((c) => {
      const t = c.trim();
      if (t) codes.add(t.toUpperCase());
    });
    return codes;
  }

  /** True only when code matches theme setting `promo_gift_code` (Tier 2 custom link codes). */
  function matchesPromoGiftCodeSetting(code) {
    if (!code) return false;
    return parseTier2Codes(getSettings()).has(String(code).trim().toUpperCase());
  }

  function extractDiscountCodeFromReferrer() {
    try {
      const ref = document.referrer || '';
      if (!ref) return null;
      const refUrl = new URL(ref);
      if (!hostsAreSameStore(refUrl.hostname, window.location.hostname)) return null;
      const match =
        refUrl.pathname.match(/\/discount\/([^/?#]+)/i) ||
        refUrl.pathname.match(/\/discounts\/([^/?#]+)/i);
      return match && match[1] ? decodeURIComponent(match[1]) : null;
    } catch (_) {
      return null;
    }
  }

  function getSettings() {
    return window.freeGiftSettings || {};
  }

  function isFeatureEnabled() {
    const s = getSettings();
    return s.enabled === true || (s.promoGift && s.promoGift.enabled === true);
  }

  function getAppliedTier2Code() {
    try {
      const fromSession = sessionStorage.getItem(TIER2_CODE_STORAGE);
      const fromLocal = localStorage.getItem(TIER2_CODE_STORAGE);
      const code = (fromSession || fromLocal || '').trim().toUpperCase();
      if (!code) return null;
      const codes = parseTier2Codes(getSettings());
      if (!codes.has(code)) return null;
      return code;
    } catch (_) {
      return null;
    }
  }

  function setAppliedTier2Code(code) {
    if (!code) return;
    const upper = String(code).trim().toUpperCase();
    try {
      sessionStorage.setItem(TIER2_CODE_STORAGE, upper);
      localStorage.setItem(TIER2_CODE_STORAGE, upper);
    } catch (_) {}
  }

  function normalizeStoreHostname(hostname) {
    return String(hostname || '')
      .replace(/^www\./i, '')
      .toLowerCase();
  }

  function hostsAreSameStore(a, b) {
    return normalizeStoreHostname(a) === normalizeStoreHostname(b);
  }

  function readBrowserCookie(name) {
    try {
      const wanted = String(name || '').trim().toLowerCase();
      if (!wanted) return null;
      const parts = document.cookie.split(';');
      for (const part of parts) {
        const trimmed = part.trim();
        const eq = trimmed.indexOf('=');
        if (eq < 0) continue;
        const key = trimmed.slice(0, eq).trim().toLowerCase();
        if (key !== wanted) continue;
        return decodeURIComponent(trimmed.slice(eq + 1).trim());
      }
    } catch (_) {}
    return null;
  }

  function writeBrowserCookie(name, value, maxAgeSeconds) {
    try {
      const key = String(name || '').trim();
      if (!key) return;
      let cookie = key + '=' + encodeURIComponent(String(value != null ? value : '')) + ';path=/;SameSite=Lax';
      if (maxAgeSeconds > 0) {
        cookie += ';max-age=' + Math.round(maxAgeSeconds);
      }
      document.cookie = cookie;
    } catch (_) {}
  }

  function clearBrowserCookie(name) {
    try {
      const key = String(name || '').trim();
      if (!key) return;
      document.cookie = key + '=;path=/;max-age=0;SameSite=Lax';
    } catch (_) {}
  }

  /** Shopify sets `discount_code` when a customer visits /discount/CODE links. */
  function readShopifyDiscountCodeCookie() {
    return readBrowserCookie('discount_code');
  }

  function clearShopifyDiscountCodeCookie() {
    clearBrowserCookie('discount_code');
  }

  function getPendingTier2Code() {
    try {
      const fromSession = sessionStorage.getItem(TIER2_PENDING_STORAGE);
      const fromLocal = localStorage.getItem(TIER2_PENDING_STORAGE);
      let code = (fromSession || fromLocal || readBrowserCookie(TIER2_PENDING_STORAGE) || '')
        .trim()
        .toUpperCase();
      if (!code) {
        const shopifyDiscount = readShopifyDiscountCodeCookie();
        if (shopifyDiscount && matchesPromoGiftCodeSetting(shopifyDiscount)) {
          code = String(shopifyDiscount).trim().toUpperCase();
        }
      }
      if (!code) return null;
      if (!matchesPromoGiftCodeSetting(code)) return null;
      return code;
    } catch (_) {
      return null;
    }
  }

  function setPendingTier2Code(code) {
    if (!code || !matchesPromoGiftCodeSetting(code)) return false;
    const upper = String(code).trim().toUpperCase();
    try {
      sessionStorage.setItem(TIER2_PENDING_STORAGE, upper);
      localStorage.setItem(TIER2_PENDING_STORAGE, upper);
      writeBrowserCookie(TIER2_PENDING_STORAGE, upper, 60 * 60 * 24 * 30);
      log('Stored pending Tier 2 code from discount link:', upper);
      return true;
    } catch (_) {
      return false;
    }
  }

  function clearPendingTier2Code() {
    try {
      sessionStorage.removeItem(TIER2_PENDING_STORAGE);
      localStorage.removeItem(TIER2_PENDING_STORAGE);
      clearBrowserCookie(TIER2_PENDING_STORAGE);
    } catch (_) {}
  }

  /**
   * Capture Tier 2 codes only when they match theme setting `promo_gift_code`.
   * Native Shopify discount links (e.g. HELLO-22378) are ignored here — those stay
   * on the existing disocunt-minicart.js flow.
   */
  function captureTier2CodeFromLanding() {
    if (!isFeatureEnabled() || getAppliedTier2Code()) return false;

    const tryStorePromoGiftCode = (raw) => {
      const code = raw != null ? String(raw).trim() : '';
      if (!code || !matchesPromoGiftCodeSetting(code)) return false;
      return setPendingTier2Code(code);
    };

    try {
      const ckT2 = new URLSearchParams(window.location.search || '').get('ck_t2');
      if (tryStorePromoGiftCode(ckT2)) return true;
    } catch (_) {}

    if (tryStorePromoGiftCode(extractDiscountCodeFromReferrer())) return true;

    if (tryStorePromoGiftCode(readShopifyDiscountCodeCookie())) return true;

    return false;
  }

  function clearAllTier2CodeState() {
    clearPendingTier2Code();
    clearAppliedTier2Code();
    clearShopifyDiscountCodeCookie();
  }

  /**
   * True when cart has an applicable native Shopify promo (WELCOME/ENJOY).
   * Tier 2 theme codes are never native — they only live in theme storage.
   */
  function cartHasNativeShopifyPromo(cart) {
    if (!cart || !Array.isArray(cart.discount_codes)) return false;
    return cart.discount_codes.some((dc) => {
      if (!dc || dc.applicable !== true) return false;
      const code = String(dc.code || '').trim().toUpperCase();
      if (!code) return false;
      if (matchesPromoGiftCodeSetting(code)) return false;
      return true;
    });
  }

  /**
   * When a pending code exists and the cart meets the Tier 2 threshold, promote it to
   * the applied storage key (discount pill + gift eligibility) and clear pending.
   * Never promote while a native Shopify promo is active (one customer promo at a time).
   */
  function promotePendingTier2IfReady(freshCart) {
    const pending = getPendingTier2Code();
    if (!pending || getAppliedTier2Code()) return false;
    if (cartHasNativeShopifyPromo(freshCart)) return false;
    if (!meetsTier2PromotionThreshold(freshCart)) return false;

    setAppliedTier2Code(pending);
    clearPendingTier2Code();
    clearShopifyDiscountCodeCookie();
    try {
      document.dispatchEvent(new CustomEvent('free-gift:tier2-code-changed'));
    } catch (_) {}
    log('Promoted pending Tier 2 code to applied:', pending);
    return true;
  }

  function clearAppliedTier2Code() {
    try {
      sessionStorage.removeItem(TIER2_CODE_STORAGE);
      localStorage.removeItem(TIER2_CODE_STORAGE);
    } catch (_) {}
    // Drop Save-stash entries keyed by gift_ GIDs so a removed Tier 2 gift cannot repaint.
    try {
      const map = window.__multishipPendingRecipientShipPropsByGid;
      if (map && typeof map === 'object') {
        Object.keys(map).forEach((k) => {
          if (String(k).startsWith('gift_')) delete map[k];
        });
      }
    } catch (_) {}
    // Clear assign-to-bundle Tier 2 markers so paid same-SKU is never re-gifted.
    try {
      if (typeof window.multishipClearAssignedTier2Gift === 'function') {
        window.multishipClearAssignedTier2Gift();
      } else if (window.__multishipAssignedTier2GiftByGid && typeof window.__multishipAssignedTier2GiftByGid.clear === 'function') {
        window.__multishipAssignedTier2GiftByGid.clear();
      }
    } catch (_) {}
  }

  /** Move applied Tier 2 back to pending so discount-link visits can re-unlock at threshold. */
  function demoteAppliedTier2ToPending() {
    const code = getAppliedTier2Code();
    if (!code) return false;
    clearAppliedTier2Code();
    setPendingTier2Code(code);
    clearShopifyDiscountCodeCookie();
    return true;
  }

  /**
   * Drop the multi-ship "assigned Tier 2 gift" stash for ONE bundle GID (not the whole map).
   * Called whenever a Tier 2 gift line/component is actually removed from that specific bundle
   * (code-driven cleanup here, or manual remove / reassign in multi-ship-new.js) so a later
   * rebuild can never reuse the stale variant+props to re-tag a different (e.g. paid same-SKU)
   * product left in that bundle as a Tier 2 gift.
   */
  function clearMultishipTier2AssignStash(gid) {
    try {
      const g = String(gid || '').trim();
      if (!g) return;
      if (typeof window.multishipClearAssignedTier2Gift === 'function') {
        window.multishipClearAssignedTier2Gift(g);
      } else if (window.__multishipAssignedTier2GiftByGid && typeof window.__multishipAssignedTier2GiftByGid.delete === 'function') {
        window.__multishipAssignedTier2GiftByGid.delete(g);
      }
    } catch (_) {}
  }

  function newGiftGid() {
    return 'gift_' + Date.now() + '_' + Math.random().toString(36).slice(2, 10);
  }

  function getTier2CatalogPriceCents() {
    const pg = getSettings().promoGift;
    if (!pg) return 0;
    const raw = pg.variantPriceCents;
    const n = Number(raw);
    return !isNaN(n) && n >= 0 ? Math.round(n) : 0;
  }

  /** Canonical Tier 2 gift line properties for cart/add.js and cart/change.js. */
  function buildTier2GiftLineProperties(overrides) {
    const base = propsToObject(overrides);
    // Gift merchandise must not carry shipping recipient fields — those live on the
    // shipping component. Never call this builder for shipping SKUs / CT parents.
    delete base._address;
    delete base._shipping;
    delete base._delivery_date;
    delete base.Fees;
    delete base.fees;
    delete base.Shipping;
    try {
      delete base['Delivery date'];
      delete base['Card message'];
      delete base['Card image url'];
      delete base.card_occasions;
    } catch (_) {}
    const pg = getSettings().promoGift;
    const code =
      getAppliedTier2Code() ||
      base._Tier_2_Discount_code ||
      (pg && pg.code) ||
      '';
    const priceCents = getTier2CatalogPriceCents();

    return Object.assign(base, {
      _gid: base._gid != null ? String(base._gid) : newGiftGid(),
      _product: 'true',
      _free_gift_included: 'true',
      _free_gift_tier: '2',
      _Tier_2_Discount_code: String(code).trim().toUpperCase(),
      _Free_gift_price: String(priceCents),
    });
  }

  function buildTier1GiftLineProperties(overrides) {
    const base = propsToObject(overrides);
    delete base._address;
    delete base._shipping;
    delete base._delivery_date;
    delete base.Fees;
    delete base.fees;
    delete base.Shipping;
    try {
      delete base['Delivery date'];
      delete base['Card message'];
      delete base['Card image url'];
      delete base.card_occasions;
    } catch (_) {}
    return Object.assign(base, {
      _gid: base._gid != null ? String(base._gid) : newGiftGid(),
      _product: 'true',
      _free_gift_included: 'true',
      _free_gift_tier: '1',
      _free_gift: 'true',
    });
  }

  /** Convert cart properties (object or array-of-pairs) to a plain object. */
  function propsToObject(props) {
    if (!props) return {};
    if (Array.isArray(props)) {
      const out = {};
      props.forEach((pr) => {
        if (Array.isArray(pr) && pr[0] != null) out[String(pr[0])] = pr[1];
      });
      return out;
    }
    if (typeof props === 'object') return Object.assign({}, props);
    return {};
  }

  const GIFT_IDENTITY_PROP_KEYS_LOCAL = [
    '_free_gift_included',
    '_free_gift_tier',
    '_free_gift',
    '_free_gift_promo',
    '_Tier_2_Discount_code',
    '_free_gift_discount_code',
    '_Free_gift_price',
    '_free_gift_price',
  ];

  /**
   * Shipping props for a gift upgrade re-add — strip gift identity, force shipping shape.
   * Local sanitizer only (do not call multi-ship helpers from free-gift).
   */
  function sanitizeGiftUpgradeShippingProps(rawProps, gid) {
    const props = propsToObject(rawProps);
    GIFT_IDENTITY_PROP_KEYS_LOCAL.forEach((k) => {
      try {
        delete props[k];
      } catch (_) {}
    });
    try {
      delete props._product;
    } catch (_) {}
    props._shipping = 'true';
    props._gid = String(gid || props._gid || '');

    if (props._address != null && typeof props._address === 'string') {
      try {
        const parsed = JSON.parse(props._address);
        if (parsed && typeof parsed === 'object') props._address = parsed;
      } catch (_) {}
    }
    return props;
  }

  function addressLooksFilled(addr) {
    if (!addr) return false;
    let a = addr;
    if (typeof a === 'string') {
      try {
        a = JSON.parse(a);
      } catch (_) {
        return String(a).trim().length > 8;
      }
    }
    if (typeof a !== 'object') return false;
    return !!(
      String(a.address1 || '').trim() ||
      String(a.zip || '').trim() ||
      String(a.first_name || '').trim()
    );
  }

  function shippingPropsHaveRecipient(props) {
    const p = propsToObject(props);
    if (addressLooksFilled(p._address)) return true;
    if (String(p['Card message'] || p.card_message || '').trim()) return true;
    if (String(p._delivery_date || p['Delivery date'] || '').trim()) return true;
    return false;
  }

  function enrichShipPropsFromLocalStorage(gid, shipProps) {
    const out = propsToObject(shipProps);
    const g = String(gid || '');
    if (!g) return out;
    try {
      if (!addressLooksFilled(out._address)) {
        const raw = localStorage.getItem('savedAddress_' + g);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (addressLooksFilled(parsed)) out._address = parsed;
        }
      }
      if (!String(out['Card message'] || out.card_message || '').trim()) {
        const msg = localStorage.getItem('cardMessage_' + g);
        if (msg && String(msg).trim()) out['Card message'] = msg;
      }
      if (!String(out._delivery_date || out['Delivery date'] || '').trim()) {
        const rawDate = localStorage.getItem('selectedDate_' + g);
        if (rawDate) {
          try {
            const d = JSON.parse(rawDate);
            const text =
              (d && (d.formatted || d.date || d.delivery_date || d.value)) || String(rawDate);
            if (String(text).trim()) {
              out._delivery_date = String(text).trim();
              out['Delivery date'] = String(text).trim();
            }
          } catch (_) {
            if (String(rawDate).trim()) {
              out._delivery_date = String(rawDate).trim();
              out['Delivery date'] = String(rawDate).trim();
            }
          }
        }
      }
    } catch (_) {}
    return out;
  }

  function stashGiftUpgradePendingShipProps(gid, shipProps) {
    const g = String(gid || '').trim();
    if (!g || !shipProps) return;
    try {
      if (!window.__multishipPendingRecipientShipPropsByGid) {
        window.__multishipPendingRecipientShipPropsByGid = Object.create(null);
      }
      window.__multishipPendingRecipientShipPropsByGid[g] = Object.assign({}, shipProps);
    } catch (_) {}
  }

  function findShippingLineForGid(gid, cart) {
    const g = String(gid || '').trim();
    if (!g) return null;

    const consider = (item) => {
      if (!item) return null;
      if (String(getProp(item, '_gid') || '') !== g) return null;
      if (isShippingMerchLine(item)) return item;
      if (getProp(item, '_shipping') === 'true' || getProp(item, '_shipping') === true) {
        return item;
      }
      return null;
    };

    for (const item of (cart && cart.items) || []) {
      const top = consider(item);
      if (top) return top;
      if (Array.isArray(item.item_components)) {
        for (const comp of item.item_components) {
          const hit = consider(comp);
          if (hit) return hit;
        }
      }
    }

    try {
      const map = window.cartItemComponents;
      if (map && typeof map === 'object') {
        for (const bundle of Object.values(map)) {
          if (!bundle || !Array.isArray(bundle.components)) continue;
          if (bundle.gid != null && String(bundle.gid) !== g) continue;
          for (const comp of bundle.components) {
            const hit = consider(comp);
            if (hit) return hit;
          }
        }
      }
    } catch (_) {}

    return null;
  }

  /**
   * Before Tier 1 ↔ Tier 2 gift swap: capture gift `_gid` + shipping recipient props so address /
   * card message / delivery date survive remove+re-add (different SKUs per tier).
   */
  function collectGiftRecipientUpgradeContext(cart, fromTier) {
    const tierStr = String(fromTier);
    const giftCandidates = [];

    const pushGift = (item) => {
      if (!item || !itemIsTierGift(item, tierStr)) return;
      const gid = String(getProp(item, '_gid') || '').trim();
      if (!gid) return;
      giftCandidates.push({ item, gid });
    };

    for (const item of (cart && cart.items) || []) {
      pushGift(item);
      if (Array.isArray(item.item_components)) {
        item.item_components.forEach(pushGift);
      }
    }

    try {
      const map = window.cartItemComponents;
      if (map && typeof map === 'object') {
        for (const bundle of Object.values(map)) {
          if (!bundle || !Array.isArray(bundle.components)) continue;
          bundle.components.forEach(pushGift);
        }
      }
    } catch (_) {}

    if (!giftCandidates.length) return null;

    let best = null;
    for (const cand of giftCandidates) {
      const ship = findShippingLineForGid(cand.gid, cart);
      let shipProps = ship ? propsToObject(ship.properties) : {};
      shipProps = enrichShipPropsFromLocalStorage(cand.gid, shipProps);
      const score =
        (addressLooksFilled(shipProps._address) ? 8 : 0) +
        (shippingPropsHaveRecipient(shipProps) ? 4 : 0) +
        (cand.gid.startsWith('gift_') ? 2 : 0) +
        (ship ? 1 : 0);
      if (!best || score > best.score) {
        best = {
          score,
          gid: cand.gid,
          shippingVariantId: ship
            ? ship.variant_id != null
              ? ship.variant_id
              : ship.id
            : null,
          shippingProperties: shipProps,
        };
      }
    }

    if (!best || !best.gid) return null;
    if (!shippingPropsHaveRecipient(best.shippingProperties) && !best.gid.startsWith('gift_')) {
      return null;
    }

    log(
      `Captured gift recipient upgrade context from tier ${tierStr}: gid=${best.gid}` +
        (shippingPropsHaveRecipient(best.shippingProperties) ? ' (has recipient)' : ' (gid only)')
    );
    return {
      gid: best.gid,
      shippingVariantId: best.shippingVariantId,
      shippingProperties: best.shippingProperties,
      fromTier: tierStr,
    };
  }

  function giftBundleHasShippingForGid(cart, gid) {
    return !!findShippingLineForGid(gid, cart);
  }

  async function readdPreservedGiftShipping(context) {
    if (!context || !context.gid) return false;
    const gid = String(context.gid);
    const shipProps = sanitizeGiftUpgradeShippingProps(context.shippingProperties || {}, gid);
    if (!shippingPropsHaveRecipient(shipProps)) {
      stashGiftUpgradePendingShipProps(gid, shipProps);
      return false;
    }

    stashGiftUpgradePendingShipProps(gid, shipProps);

    const fresh = await getFreshCart();
    if (giftBundleHasShippingForGid(fresh, gid)) {
      log(`Gift shipping already present for gid=${gid} after upgrade — skip re-add`);
      return false;
    }

    let variantId = context.shippingVariantId;
    if (!variantId) {
      // Default to known expedited shipping variant when the prior line was lost.
      variantId = 44113130520646;
    }

    log(`Re-adding preserved gift shipping for upgraded gift gid=${gid}`, shipProps);
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: [{ id: Number(variantId) || variantId, quantity: 1, properties: shipProps }],
        }),
      });
      if (!res.ok) {
        console.error('[FreeGift] preserved shipping cart/add.js failed', res.status);
        return false;
      }
      await pause(400);
      return true;
    } catch (err) {
      console.error('[FreeGift] preserved shipping cart/add.js error', err);
      return false;
    }
  }

  function tier2GiftPropertiesNeedRepair(item) {
    if (!item || !isConfiguredTierGiftVariant(item, '2')) return false;
    const applied = getAppliedTier2Code();
    if (!applied) return false;

    const code = getGiftProp(item, '_Tier_2_Discount_code', '_free_gift_discount_code');
    const included = getProp(item, '_free_gift_included');
    const tier = getProp(item, '_free_gift_tier');
    const price = getGiftProp(item, '_Free_gift_price', '_free_gift_price');

    if (!code || String(code).trim().toUpperCase() !== applied) return true;
    if (included !== 'true' && included !== true) return true;
    if (String(tier) !== '2') return true;
    if (price == null || String(price).trim() === '') return true;
    const addr = getProp(item, '_address');
    if (addr != null && String(addr).trim() !== '') return true;
    return false;
  }

  function isAssignedTier2GiftFromMultishipStash(item) {
    try {
      const map = window.__multishipAssignedTier2GiftByGid;
      if (!map || typeof map.get !== 'function') return false;
      const gid = String(getProp(item, '_gid') || '');
      if (!gid) return false;
      const entry = map.get(gid);
      if (!entry) return false;
      if (entry.at && Date.now() - entry.at > 45 * 60 * 1000) return false;
      const vid = String(item.variant_id != null ? item.variant_id : item.id || '');
      const lk = String(item.line_key || item.key || '');
      if (entry.lineKey && lk && lk === String(entry.lineKey)) return true;
      if (String(entry.variantId) !== vid) return false;
      // Same variant in this GID is the assigned gift (paid same-SKU uses a different GID).
      return true;
    } catch (_) {
      return false;
    }
  }

  function isTier2GiftRepairCandidate(item) {
    if (!item || isShippingMerchLine(item)) return false;
    // NEVER cart/change Cart Transform parents — that replaces the line and wipes
    // shipping recipient props on the sibling shipping component.
    if (isCartTransformBundleParentLine(item)) return false;
    if (!isConfiguredTierGiftVariant(item, '2')) return false;
    if (!tier2GiftPropertiesNeedRepair(item)) return false;
    if (getProp(item, '_free_gift_included') === 'true' || getProp(item, '_free_gift_included') === true) {
      return true;
    }
    const gid = String(getProp(item, '_gid') || '');
    if (gid.startsWith('gift_')) return true;
    if (
      getProp(item, '_free_gift') === 'true' ||
      getProp(item, '_free_gift') === true ||
      getProp(item, '_free_gift_promo') === 'true' ||
      getProp(item, '_free_gift_promo') === true
    ) {
      return true;
    }
    // After assign-to-existing-bundle, CT strips props and replaces gift_ GID with the
    // paid bundle GID. Use the multiship assign stash — never variant-only globally.
    if (isAssignedTier2GiftFromMultishipStash(item)) return true;
    return isZeroPriceLine(item);
  }

  function flattenCartLinesForPropertyRepair(cart) {
    const flat = mergeCartData(cart);
    const list = [];
    const seen = new Set();

    for (const item of flat) {
      if (!item) continue;
      // Prefer component line_key; never use a CT parent key for property repair.
      if (isCartTransformBundleParentLine(item)) continue;
      if (isShippingMerchLine(item)) continue;
      const lineKey = item.line_key || item.key;
      if (!lineKey || seen.has(lineKey)) continue;
      seen.add(lineKey);
      list.push({
        lineKey,
        qty: Number(item.quantity) || 1,
        properties: propsToObject(item.properties),
        item,
      });
    }
    return list;
  }

  async function patchCartLineProperties(lineKey, qty, properties) {
    if (!lineKey) return false;
    // Belt-and-suspenders: refuse known shipping keys / CT parent map keys.
    try {
      const map = window.cartItemComponents;
      if (map && map[String(lineKey)] && Array.isArray(map[String(lineKey)].components)
          && map[String(lineKey)].components.length > 0) {
        log('Skip Tier 2 property patch — lineKey is CT bundle parent', lineKey);
        return false;
      }
    } catch (_) {}
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: lineKey, quantity: qty, properties }),
      });
      return res.ok;
    } catch (_) {
      return false;
    }
  }

  /**
   * Re-apply Tier 2 identity props when Cart Transform / multiship rebuild dropped them.
   * Merges onto existing merchandise props only — never shipping lines or CT parents
   * (Shopify cart/change replaces the full properties object).
   */
  async function ensureTier2GiftPropertiesOnCart(cart) {
    if (!getAppliedTier2Code()) return false;
    if (determineEligibleTier(cart) !== 'tier2') return false;

    const lines = flattenCartLinesForPropertyRepair(cart);
    let patched = false;

    for (const row of lines) {
      if (!isTier2GiftRepairCandidate(row.item)) continue;

      const existing = propsToObject(row.properties);
      // Preserve non-gift keys on the merchandise line; only stamp Tier 2 identity.
      const merged = buildTier2GiftLineProperties(existing);
      log('Repairing Tier 2 gift properties on merchandise line', row.lineKey);

      const ok = await patchCartLineProperties(row.lineKey, row.qty, merged);
      if (ok) patched = true;
      await pauseGiftCart(280);
    }

    return patched;
  }

  function isTier2ThemeCode(code) {
    return matchesPromoGiftCodeSetting(code);
  }

  /** B4 — fresh cart + refreshed cartItemComponents */
  async function getFreshCart() {
    await refreshCartItemComponents();
    const res = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
    if (!res.ok) return null;
    return res.json();
  }

  /** True when a gift line key or gift `_gid` is still present in live cart.js / cartItemComponents. */
  function giftIdentityStillInLiveCart(freshCart, opts) {
    const lineKey = opts && opts.lineKey != null ? String(opts.lineKey).trim() : '';
    const parentKey = opts && opts.parentKey != null ? String(opts.parentKey).trim() : '';
    const gid = opts && opts.gid != null ? String(opts.gid).trim() : '';
    const items = freshCart && Array.isArray(freshCart.items) ? freshCart.items : [];

    // A bare (non-`gift_`) GID is a bundle-level id shared by every line in that bundle —
    // shipping, paid mains, AND any gift. Matching on it alone is not proof the gift line
    // survives: a paid same-SKU product left in the same bundle after the real gift was
    // removed would match forever. Only a `gift_`-prefixed GID (the CT-safe standalone-gift
    // marker used everywhere else in this file) is trustworthy by itself. Line-key equality
    // stays safe unconditionally — Shopify bakes the properties hash into the key, so a
    // stripped-down gift and a differently-propertied paid line can never share one.
    const matchGid = (props) => {
      if (!gid || !gid.startsWith('gift_')) return false;
      try {
        if (Array.isArray(props)) {
          const pair = props.find((pr) => Array.isArray(pr) && String(pr[0]) === '_gid');
          return pair && String(pair[1] || '').trim() === gid;
        }
        return props && String(props._gid || '').trim() === gid;
      } catch (_) {
        return false;
      }
    };

    const matchLineKey = (it) => {
      if (!it || !lineKey) return false;
      return String(it.key || '') === lineKey || String(it.line_key || '') === lineKey;
    };

    // Prefer exact gift line / gift_ GID. Do not treat "parent key still in cart" alone as
    // proof — the parent can remain after the gift component was removed (or never added).
    for (const item of items) {
      if (matchLineKey(item) || matchGid(item.properties)) return true;
      if (Array.isArray(item.item_components)) {
        for (const comp of item.item_components) {
          if (matchLineKey(comp) || matchGid(comp.properties)) return true;
        }
      }
      // Gift-only opaque parent: same key + gift_ GID (or line key equals parent).
      if (
        parentKey &&
        String(item.key || '') === parentKey &&
        gid.startsWith('gift_') &&
        matchGid(item.properties)
      ) {
        return true;
      }
    }

    try {
      const map = window.cartItemComponents;
      if (map && typeof map === 'object') {
        for (const [pk, bundle] of Object.entries(map)) {
          if (gid && bundle && String(bundle.gid || '') === gid && gid.startsWith('gift_')) {
            return true;
          }
          if (!bundle || !Array.isArray(bundle.components)) continue;
          for (const comp of bundle.components) {
            if (matchLineKey(comp) || matchGid(comp.properties)) return true;
          }
          if (
            parentKey &&
            String(pk) === parentKey &&
            gid.startsWith('gift_') &&
            String(bundle.gid || '') === gid
          ) {
            return true;
          }
        }
      }
    } catch (_) {}

    return false;
  }

  /**
   * B1 — gift exists in cart (top-level + bundle components + verified secondary sources).
   * Liquid `cartWithComp` / initial DOM are page-load snapshots and can go stale after cart
   * mutations — never treat them as authoritative when live cart.js + cartItemComponents
   * already confirmed the gift is absent (that blocked Tier 1 auto-add at $130+).
   */
  function giftExistsInCart(tier, freshCart) {
    const tierStr = String(tier);
    const items = freshCart && freshCart.items ? freshCart.items : [];
    const hasFreshCart = !!(freshCart && Array.isArray(freshCart.items));

    for (const item of items) {
      if (itemIsTierGift(item, tierStr)) {
        log(`giftExistsInCart tier ${tierStr}: found on cart.js line`, item.key || item.line_key);
        return true;
      }
      if (Array.isArray(item.item_components)) {
        for (const comp of item.item_components) {
          if (itemIsTierGift(comp, tierStr)) {
            log(`giftExistsInCart tier ${tierStr}: found in cart.js item_components`);
            return true;
          }
        }
      }
    }

    try {
      const map = window.cartItemComponents;
      if (map && typeof map === 'object') {
        for (const [parentKey, bundle] of Object.entries(map)) {
          if (!bundle || !Array.isArray(bundle.components)) continue;
          for (const comp of bundle.components) {
            if (itemIsTierGift(comp, tierStr)) {
              log(
                `giftExistsInCart tier ${tierStr}: found in cartItemComponents bundle ${parentKey}`,
                comp.line_key || comp.key
              );
              return true;
            }
          }
        }
      }
    } catch (_) {}

    if (Array.isArray(window.cartWithComp)) {
      for (const row of window.cartWithComp) {
        if (!row || !Array.isArray(row.components)) continue;
        const parentKey = row.cart && row.cart.key != null ? String(row.cart.key) : '';
        for (const comp of row.components) {
          if (!itemIsTierGift(comp, tierStr)) continue;
          const lineKey = comp.line_key || comp.key || '';
          const gid = String(getProp(comp, '_gid') || '');
          if (
            hasFreshCart &&
            !giftIdentityStillInLiveCart(freshCart, { lineKey, parentKey, gid })
          ) {
            log(
              `giftExistsInCart tier ${tierStr}: ignoring stale cartWithComp gift (line=${lineKey || parentKey})`
            );
            continue;
          }
          log(`giftExistsInCart tier ${tierStr}: found in cartWithComp`);
          return true;
        }
      }
    }

    try {
      const techEls = document.querySelectorAll('.tech-cart-info[data-item-components-data]');
      for (const el of techEls) {
        const raw = el.getAttribute('data-item-components-data');
        if (!raw) continue;
        let comps;
        try {
          comps = JSON.parse(raw);
        } catch (_) {
          continue;
        }
        if (!Array.isArray(comps)) continue;
        const parentKey =
          el.getAttribute('data-key') ||
          (el.closest('[data-key]') && el.closest('[data-key]').getAttribute('data-key')) ||
          '';
        for (const comp of comps) {
          if (!itemIsTierGift(comp, tierStr)) continue;
          const lineKey = comp.line_key || comp.key || '';
          const gid = String(getProp(comp, '_gid') || '');
          if (
            hasFreshCart &&
            !giftIdentityStillInLiveCart(freshCart, { lineKey, parentKey, gid })
          ) {
            log(
              `giftExistsInCart tier ${tierStr}: ignoring stale DOM tech-cart-info gift (line=${lineKey || parentKey})`
            );
            continue;
          }
          log(`giftExistsInCart tier ${tierStr}: found in DOM tech-cart-info`);
          return true;
        }
      }
    } catch (_) {}

    // Shipping-page row markers: after CT strips component props, Liquid may still mark the
    // gift row via gift_ GID / detect snippet. Avoid re-adding when those markers are present
    // AND the live cart still has that gift identity.
    try {
      const rows = document.querySelectorAll(
        'multiship-product[data-free-gift-line="true"], multiship-product[data-gid^="gift_"]'
      );
      for (const row of rows) {
        const gid = String(row.getAttribute('data-gid') || '');
        const rowKey = String(row.getAttribute('data-key') || row.getAttribute('data-parent-key') || '');
        const vid =
          row.getAttribute('data-variant-id') ||
          (row.querySelector('[data-quantity-variant-id]') &&
            row.querySelector('[data-quantity-variant-id]').getAttribute('data-quantity-variant-id')) ||
          '';
        let matched = false;
        if (tierStr === '2') {
          if (gid.startsWith('gift_') && isConfiguredTierGiftVariant({ variant_id: vid, id: vid }, '2')) {
            matched = true;
          } else if (
            row.getAttribute('data-free-gift-line') === 'true' &&
            isConfiguredTierGiftVariant({ variant_id: vid, id: vid }, '2')
          ) {
            matched = true;
          }
        } else if (tierStr === '1') {
          if (
            row.getAttribute('data-free-gift-line') === 'true' &&
            isConfiguredTierGiftVariant({ variant_id: vid, id: vid }, '1')
          ) {
            matched = true;
          } else if (
            gid.startsWith('gift_') &&
            isConfiguredTierGiftVariant({ variant_id: vid, id: vid }, '1')
          ) {
            matched = true;
          }
        }
        if (!matched) continue;
        // Assigned-into-paid-bundle gifts use a paid `_gid` (not gift_). giftIdentityStillInLiveCart
        // refuses bare paid GIDs by design, and CT key rotation drops data-key matches — so a live
        // free-gift row was ignored while the progress banner already said under threshold.
        // Trust the matched shipping-page marker itself (do not call cartHasLikelyExistingTierGift —
        // that re-enters giftExistsInCart and can recurse).
        if (
          hasFreshCart &&
          !giftIdentityStillInLiveCart(freshCart, { lineKey: rowKey, parentKey: rowKey, gid })
        ) {
          let onShipping = false;
          try {
            onShipping = !!document.querySelector('shipping-page');
          } catch (_) {}
          if (!onShipping) {
            log(
              `giftExistsInCart tier ${tierStr}: ignoring stale DOM gift row (gid=${gid || rowKey})`
            );
            continue;
          }
          log(
            `giftExistsInCart tier ${tierStr}: trusting visible free-gift row despite paid/rotated identity gid=${gid || rowKey}`
          );
        }
        log(`giftExistsInCart tier ${tierStr}: found via DOM gift row gid=${gid || rowKey}`);
        return true;
      }
    } catch (_) {}

    log(`giftExistsInCart tier ${tierStr}: not found`);
    return false;
  }

  /**
   * True when the configured gift SKU is already in the cart as a gift-like line
   * (gift_ GID, gift props, or $0). Used to block false re-add on page refresh when
   * giftExistsInCart briefly fails due to CT / cartItemComponents lag.
   *
   * Tier 2: NEVER match by variant alone (paid same-SKU must stay editable) — only
   * gift markers / gift_ GID / $0 with gift props.
   */
  function cartHasLikelyExistingTierGift(cart, tier) {
    const tierStr = String(tier);
    if (giftExistsInCart(tierStr, cart)) return true;

    const ids = getTierVariantIds(tierStr);
    if (!ids.size) return false;

    const looksLikeGiftLine = (item) => {
      if (!item || isShippingMerchLine(item)) return false;
      const vid =
        item.variant_id != null
          ? String(item.variant_id)
          : item.id != null
            ? String(item.id)
            : '';
      if (!vid || !ids.has(vid)) return false;

      const gid = String(getProp(item, '_gid') || '');
      if (gid.startsWith('gift_')) return true;
      if (isGiftMarkedLine(item) || lineMatchesGiftTier(item, tierStr)) return true;
      if (tierStr === '2') {
        // Paid same-SKU Biscotti is non-zero and non-gift_ — do not treat as existing gift.
        return isTier2GiftLine(item) || (isZeroPriceLine(item) && isGiftMarkedLine(item));
      }
      // Tier 1 gift product is typically $0 and not sold as a parallel paid SKU in this theme.
      if (isZeroPriceLine(item)) return true;
      return false;
    };

    const items = cart && Array.isArray(cart.items) ? cart.items : [];
    for (const item of items) {
      if (looksLikeGiftLine(item)) return true;
      if (Array.isArray(item.item_components)) {
        for (const comp of item.item_components) {
          if (looksLikeGiftLine(comp)) return true;
        }
      }
    }

    try {
      const map = window.cartItemComponents;
      if (map && typeof map === 'object') {
        for (const bundle of Object.values(map)) {
          if (!bundle || !Array.isArray(bundle.components)) continue;
          for (const comp of bundle.components) {
            if (looksLikeGiftLine(comp)) return true;
          }
        }
      }
    } catch (_) {}

    return false;
  }

  function findOrphanGiftShippingGidSafe(cart) {
    try {
      const findOrphan =
        typeof window.multishipFindOrphanGiftShippingGidToReclaim === 'function'
          ? window.multishipFindOrphanGiftShippingGidToReclaim
          : null;
      if (!findOrphan || !cart || !Array.isArray(cart.items)) return null;
      const gid = findOrphan(cart.items);
      if (gid && String(gid).startsWith('gift_')) return String(gid);
    } catch (_) {}
    return null;
  }

  async function healOrphanGiftShippingSafe() {
    try {
      if (typeof window.multishipHealOrphanGiftShippingBundles === 'function') {
        const healed = await window.multishipHealOrphanGiftShippingBundles();
        if (healed) log('Healed orphan gift shipping → gift merchandise after sync guard');
        return !!healed;
      }
    } catch (e) {
      console.warn('[FreeGift] gift shipping heal failed', e);
    }
    return false;
  }

  /** B2 — qualifying main-product subtotal only */
  function calculateMainProductsTotal(freshCart) {
    const items = freshCart && freshCart.items ? freshCart.items : [];
    return resolveEffectiveMerchandiseTotalCents(items);
  }

  function determineEligibleTier(freshCart) {
    const s = getSettings();
    const tier1Min = Math.round((parseFloat(s.threshold) || 125) * 100);
    const pg = s.promoGift;
    const tier2Min =
      pg && pg.enabled ? Math.round((parseFloat(pg.threshold) || 100) * 100) : null;

    const tier2Code = getAppliedTier2Code();
    const tier2Eligible =
      tier2Min != null &&
      tier2Code != null &&
      pg &&
      pg.enabled &&
      meetsGiftThresholdForTier(freshCart, tier2Min);

    if (tier2Eligible) return 'tier2';
    if (s.enabled && meetsGiftThresholdForTier(freshCart, tier1Min)) return 'tier1';
    return 'none';
  }

  function mergeCartData(cart) {
    const flat = [];
    const seen = new Set();

    const pushIfNew = (item, parentKey = null, isComponent = false) => {
      if (!item) return;
      const idKey =
        item.line_key ||
        item.key ||
        (item.variant_id ? `${String(item.variant_id)}_${String(parentKey || '')}` : null);
      if (!idKey || seen.has(idKey)) return;
      seen.add(idKey);
      flat.push({ ...item, _parentKey: parentKey, _isComponent: isComponent });
    };

    for (const item of cart.items || []) {
      pushIfNew(item, null, false);
      if (item.item_components && item.item_components.length) {
        for (const comp of item.item_components) {
          pushIfNew(comp, item.key, true);
        }
      }
    }

    if (typeof window.cartItemComponents === 'object' && window.cartItemComponents !== null) {
      for (const [parentKey, bundle] of Object.entries(window.cartItemComponents)) {
        if (!bundle || !Array.isArray(bundle.components)) continue;
        for (const comp of bundle.components) {
          pushIfNew(comp, parentKey, true);
        }
      }
    }

    if (Array.isArray(window.cartWithComp)) {
      for (const row of window.cartWithComp) {
        const parentKey = row?.cart?.key || null;
        if (row?.components) {
          for (const comp of row.components) {
            pushIfNew(comp, parentKey, !!parentKey);
          }
        }
      }
    }

    try {
      const techEls = document.querySelectorAll('.tech-cart-info[data-item-components-data]');
      techEls.forEach((el) => {
        const raw = el.getAttribute('data-item-components-data');
        if (!raw) return;
        let comps;
        try {
          comps = JSON.parse(raw);
        } catch (_) {
          return;
        }
        if (!Array.isArray(comps)) return;
        const productEl = el.closest('multiship-product');
        const parentKey = productEl ? productEl.dataset.parentKey || null : null;
        comps.forEach((comp) => pushIfNew(comp, parentKey, !!parentKey));
      });
    } catch (_) {}

    return flat;
  }

  /**
   * Live cart.js line keys only (top-level + item_components). Stale cartItemComponents /
   * DOM snapshots after Save must not drive duplicate removal.
   */
  function collectLiveCartLineKeys(cart) {
    const keys = new Set();
    try {
      for (const item of (cart && cart.items) || []) {
        if (!item) continue;
        if (item.key) keys.add(String(item.key));
        if (item.line_key) keys.add(String(item.line_key));
        if (Array.isArray(item.item_components)) {
          for (const c of item.item_components) {
            if (!c) continue;
            if (c.key) keys.add(String(c.key));
            if (c.line_key) keys.add(String(c.line_key));
          }
        }
      }
    } catch (_) {}
    return keys;
  }

  /** True when live cart still has recipient data for this gift line's `_gid`. */
  function giftLineHasRecipientShippingInLiveCart(item, cart) {
    const gid = String(getProp(item, '_gid') || '').trim();
    if (!gid) return false;
    try {
      for (const line of (cart && cart.items) || []) {
        if (!line) continue;
        const check = (c) => {
          if (!c || !c.properties) return false;
          if (String(getProp(c, '_gid') || '') !== gid) return false;
          // After Save, CT often stamps _address onto the gift merchandise / opaque parent
          // (cart.js may show ship=false but addr=true on the gift line). Also match shipping siblings.
          return shippingPropsHaveRecipient(c.properties);
        };
        if (check(line)) return true;
        if (Array.isArray(line.item_components)) {
          for (const c of line.item_components) {
            if (check(c)) return true;
          }
        }
      }
    } catch (_) {}
    return false;
  }

  /**
   * Prefer keeping the Tier gift that still has identity props / gift_ GID / is already in a
   * bundle. Used to collapse duplicates after a page-refresh re-add race.
   * @param {object} item
   * @param {object} [cart] live cart.js — boosts lines that still exist / have recipient shipping
   * @param {Set<string>} [liveKeys]
   */
  function scoreTierGiftKeepCandidate(item, cart, liveKeys) {
    let score = 0;
    if (!item) return score;
    if (isTier2GiftLine(item) || itemIsTierGift(item, '1')) score += 10;
    const gid = String(getProp(item, '_gid') || '');
    if (gid.startsWith('gift_')) score += 8;
    if (getProp(item, '_free_gift_included') === 'true' || getProp(item, '_free_gift_included') === true) {
      score += 5;
    }
    if (getGiftProp(item, '_Tier_2_Discount_code', '_free_gift_discount_code')) score += 5;
    if (item._isComponent && item._parentKey) score += 3;
    // Prefer lines that already have a sibling shipping address (saved gift).
    try {
      if (gid) {
        const row = document.querySelector(
          `multiship-product[data-gid="${CSS && CSS.escape ? CSS.escape(gid) : gid}"] .recipient_data, multiship-product[data-gid="${gid}"] [data-has-shipping="true"]`
        );
        if (row) score += 12;
      }
    } catch (_) {}
    // Live cart beats stale snapshots — critical after Save & Continue (CT rotates keys).
    const lk = String((item.line_key || item.key) || '');
    if (liveKeys && lk && liveKeys.has(lk)) score += 40;
    if (cart && giftLineHasRecipientShippingInLiveCart(item, cart)) score += 60;
    return score;
  }

  function collectDuplicateTierGiftsToRemove(cart, tier) {
    const tierStr = String(tier);
    const liveKeys = collectLiveCartLineKeys(cart);
    const allItems = mergeCartData(cart);
    const tierGiftsAll = allItems.filter((item) => itemIsTierGift(item, tierStr));
    // Only live cart.js keys. Stale cartItemComponents / DOM tech-cart-info after Save look like
    // "duplicates" and previously caused FreeGift to qty=0 the live CT gift+shipping line while
    // "keeping" a dead pre-Save key — then re-add a bare gift with a new gift_ GID.
    const tierGifts = tierGiftsAll.filter((g) => {
      const lk = String((g && (g.line_key || g.key)) || '');
      return lk && liveKeys.has(lk);
    });
    if (tierGiftsAll.length > tierGifts.length) {
      log(
        `Duplicate tier ${tierStr} gift: ignoring ${tierGiftsAll.length - tierGifts.length} stale snapshot line(s); live count=${tierGifts.length}`
      );
    }
    if (tierGifts.length <= 1) return [];

    const bundleParentKeys = new Set(
      typeof window.cartItemComponents === 'object' && window.cartItemComponents !== null
        ? Object.keys(window.cartItemComponents)
        : []
    );

    // Rank keep-candidate once so multiple gift-only bundles (common after refresh re-add) collapse.
    const ranked = tierGifts.slice().sort(
      (a, b) =>
        scoreTierGiftKeepCandidate(b, cart, liveKeys) - scoreTierGiftKeepCandidate(a, cart, liveKeys)
    );
    const keep = ranked[0];
    const keepKey = String((keep && (keep.line_key || keep.key)) || '');
    const keepHasRecipient = giftLineHasRecipientShippingInLiveCart(keep, cart);
    const toRemove = [];
    const seen = new Set();

    for (let i = 1; i < ranked.length; i++) {
      const g = ranked[i];
      if (!g) continue;
      const lineKey = g.line_key || g.key;
      if (!lineKey || String(lineKey) === keepKey || seen.has(String(lineKey))) continue;
      // Never remove a gift that still has recipient shipping if the keep candidate does not.
      if (
        keepHasRecipient === false &&
        giftLineHasRecipientShippingInLiveCart(g, cart)
      ) {
        log(
          `Duplicate tier ${tierStr} gift: skip removing ${lineKey} — it has recipient shipping (keep candidate does not)`
        );
        continue;
      }
      if (keepHasRecipient && giftLineHasRecipientShippingInLiveCart(g, cart)) {
        // Both have recipient — only remove if clearly a second gift_ GID duplicate; otherwise skip.
        const keepGid = String(getProp(keep, '_gid') || '');
        const gGid = String(getProp(g, '_gid') || '');
        if (keepGid && gGid && keepGid === gGid) {
          // Same GID listed twice (parent + component) — removing component key can break CT; skip.
          log(
            `Duplicate tier ${tierStr} gift: skip removing ${lineKey} — same gift_ GID as keep (${keepGid})`
          );
          continue;
        }
      }
      // Never schedule a CT parent key for removal when removing a component gift — that could wipe
      // an unrelated paid product if keys were confused. Prefer component line_key.
      if (!g._isComponent && bundleParentKeys.has(g.key) && g.key === g.line_key) {
        // Opaque parent that is itself gift-only may be listed without components; skip unless no line_key alternative
        continue;
      }
      seen.add(String(lineKey));
      toRemove.push({ lineKey, key: g.key });
      log(
        `Duplicate tier ${tierStr} gift: removing extra line ${lineKey} (keeping ${keepKey || 'best candidate'})`
      );
    }

    // Legacy case: standalone + bundle gift — remove standalones that were not already queued.
    // Only live standalone keys; never touch a standalone that still has recipient shipping when
    // the keep candidate is the same gift_ GID (Save mid-CT state).
    const bundleGifts = tierGifts.filter((g) => g._isComponent && g._parentKey);
    const standaloneGifts = tierGifts.filter((g) => !g._isComponent || !g._parentKey);
    if (bundleGifts.length >= 1 && standaloneGifts.length >= 1) {
      for (const g of standaloneGifts) {
        if (!g.key || bundleParentKeys.has(g.key)) continue;
        const lk = g.line_key || g.key;
        if (!lk || seen.has(String(lk)) || String(lk) === keepKey) continue;
        if (!liveKeys.has(String(lk))) continue;
        if (giftLineHasRecipientShippingInLiveCart(g, cart)) {
          log(
            `Duplicate tier ${tierStr} gift: skip removing standalone ${lk} — has recipient shipping`
          );
          continue;
        }
        seen.add(String(lk));
        toRemove.push({ lineKey: lk, key: g.key });
        log(`Duplicate tier ${tierStr} gift: removing standalone (bundle already has gift)`);
      }
    }

    return toRemove;
  }

  function getTier2ThresholdCents() {
    const s = getSettings();
    const pg = s.promoGift;
    if (!pg || !pg.enabled) return null;
    return Math.round((parseFloat(pg.threshold) || 100) * 100);
  }

  function meetsTier2Threshold(freshCart) {
    const min = getTier2ThresholdCents();
    if (min == null) return false;
    return calculateMainProductsTotal(freshCart) >= min;
  }

  function compTitleLooksLikeShippingFee(comp) {
    const t = (comp && (comp.title || comp.product_title) ? comp.title || comp.product_title : '')
      .toString()
      .toLowerCase();
    return /shipping|gift information|shipping information|gift info/.test(t);
  }

  function lineExcludedFromProgressMerchandise(item) {
    if (!item) return true;
    if (getProp(item, '_free_gift') === 'true' || getProp(item, '_free_gift_promo') === 'true') {
      return true;
    }
    if (isGiftMarkedLine(item) || itemIsTierGift(item, '1') || itemIsTierGift(item, '2')) return true;
    if (
      getProp(item, '_shipping') === 'true' ||
      getProp(item, '_fee') === 'true' ||
      getProp(item, '_ribbon') === 'true'
    ) {
      return true;
    }
    const st = (item.product_title || item.title || '').toString().toLowerCase();
    return compTitleLooksLikeShippingFee({ title: st });
  }

  /**
   * Merchandise total at final_line_price — matches snippets/cart-progress.liquid so the
   * Tier 2 pill/gift track what the customer sees (subtotal / progress bar).
   */
  function cartMainProductFinalTotalCents(items) {
    let total = 0;
    for (const item of items || []) {
      if (!item || lineExcludedFromProgressMerchandise(item)) continue;

      if (item.item_components && item.item_components.length) {
        let lineMerch = 0;
        let lineShip = 0;
        for (const comp of item.item_components) {
          if (lineExcludedFromProgressMerchandise(comp)) continue;
          if (getProp(comp, '_shipping') === 'true' || getProp(comp, '_shipping') === true) {
            lineShip += Number(comp.final_line_price) || 0;
            continue;
          }
          if (getProp(comp, '_fee') === 'true' || getProp(comp, '_ribbon') === 'true') continue;
          if (compTitleLooksLikeShippingFee(comp)) {
            lineShip += Number(comp.final_line_price) || 0;
          } else {
            lineMerch += Number(comp.final_line_price) || 0;
          }
        }
        if (lineMerch > 0) {
          total += lineMerch;
        } else if (lineShip > 0) {
          const parentCents = Number(item.final_line_price) || 0;
          const adj = parentCents - lineShip;
          if (adj > 0) total += adj;
        } else {
          total += Number(item.final_line_price) || 0;
        }
        continue;
      }

      // Opaque CT parent: prefer cartItemComponents merch sum over whole-line final_line_price
      // (which includes shipping and falsely keeps Tier 1 gifts under threshold).
      try {
        const map = window.cartItemComponents;
        const parentKey = item.key != null ? String(item.key) : '';
        const bundle = map && parentKey ? map[parentKey] : null;
        if (bundle && Array.isArray(bundle.components) && bundle.components.length) {
          let mapMerch = 0;
          for (const comp of bundle.components) {
            if (!comp || lineExcludedFromProgressMerchandise(comp)) continue;
            if (isShippingMerchLine(comp) || isFeeOrRibbonLine(comp)) continue;
            if (!isMainProductForGiftThreshold(comp)) continue;
            mapMerch +=
              Number(comp.final_line_price) ||
              mainProductLineCents(comp) ||
              0;
          }
          if (mapMerch > 0) {
            total += Math.round(mapMerch);
            continue;
          }
        }
      } catch (_) {}

      // Standalone non-CT line only — skip opaque has_components parents with no map.
      if (item.has_components === true || item.has_components === 'true') continue;

      total += Number(item.final_line_price) || 0;
    }
    return Math.round(total);
  }


  function calculateMainProductsFinalTotal(freshCart) {
    return cartMainProductFinalTotalCents(freshCart && freshCart.items ? freshCart.items : []);
  }

  function meetsTier2FinalMerchandiseThreshold(freshCart) {
    const min = getTier2ThresholdCents();
    if (min == null) return false;
    return calculateMainProductsFinalTotal(freshCart) >= min;
  }

  /**
   * Gift threshold aligned with cart-progress.liquid (final_line_price) while preserving
   * multiship DOM Product Total lift when discounted component prices undercount.
   */
  function meetsGiftThresholdForTier(freshCart, minCents) {
    if (minCents == null || !(minCents > 0)) return false;

    // Shipping page Gift Summary "Product Total" matches the progress bar — authoritative
    // only when hydrated (ignore transient $0 during address assign / section swap).
    try {
      if (document.querySelector('shipping-page')) {
        const dom = getDOMTotalCents();
        if (dom !== null && isShippingDomMerchandiseReliable(dom, freshCart)) {
          return dom >= minCents;
        }
        // UNDER-THRESHOLD-GIFT-REMOVE-2026-09-15: hydrated under-threshold Gift Summary that
        // agrees with merchandise-only cart math must demote eligibility even when the broader
        // reliability helper still hesitates (e.g. multi-ship hint noise).
        if (dom !== null && dom > 0 && dom < minCents) {
          const merchOnly = cartMerchandiseOnlyHintCents(freshCart);
          if (
            merchOnly <= 0 ||
            merchOnly < minCents ||
            Math.abs(dom - merchOnly) <= Math.max(Math.round(minCents * 0.15), 2500)
          ) {
            return false;
          }
        }
      }
    } catch (_) {}

    if (calculateMainProductsFinalTotal(freshCart) >= minCents) return true;
    if (calculateMainProductsTotal(freshCart) < minCents) return false;
    const dom = getDOMTotalCents();
    const hasComponentsMap =
      typeof window.cartItemComponents === 'object' &&
      window.cartItemComponents !== null &&
      Object.keys(window.cartItemComponents).length > 0;
    return (
      hasComponentsMap &&
      dom != null &&
      isShippingDomMerchandiseReliable(dom, freshCart) &&
      dom >= minCents
    );
  }

  function meetsTier2PromotionThreshold(freshCart) {
    const min = getTier2ThresholdCents();
    return meetsGiftThresholdForTier(freshCart, min);
  }

  /**
   * True when an applied Tier 2 code should be demoted — final merchandise is below threshold.
   * Keeps multiship DOM/lift paths when gift_summary Product Total still qualifies.
   * Never demote from an incomplete multi-ship DOM total (one shipment) when cart is still ≥ threshold.
   */
  function shouldDemoteAppliedTier2Code(freshCart) {
    if (!getAppliedTier2Code()) return false;
    const min = getTier2ThresholdCents();
    if (min == null) return false;
    if (meetsGiftThresholdForTier(freshCart, min)) return false;

    // Shipping page: only trust a fully hydrated Gift Summary total for demotion.
    try {
      if (document.querySelector('shipping-page')) {
        const dom = getDOMTotalCents();
        if (dom !== null && !isShippingDomMerchandiseReliable(dom, freshCart)) {
          log(
            `Tier 2 demotion: shipping DOM Product Total $${(dom / 100).toFixed(2)} unreliable vs cart — checking cart merchandise only`
          );
          // Fall through to cart final/lifted check below (do not demote from partial DOM).
        } else if (dom !== null && dom < min) {
          log(
            `Tier 2 demotion (shipping DOM): Product Total $${(dom / 100).toFixed(2)} < $${(min / 100).toFixed(2)}`
          );
          return true;
        } else {
          const map = window.cartItemComponents;
          const hasComponents =
            map && typeof map === 'object' && Object.keys(map).length > 0;
          if (!hasComponents && (dom === null || !isShippingDomMerchandiseReliable(dom, freshCart))) {
            log('Tier 2 demotion deferred — shipping page merchandise totals not hydrated yet');
            return false;
          }
        }
      }
    } catch (_) {}

    const finalCents = calculateMainProductsFinalTotal(freshCart);
    const liftedCents = calculateMainProductsTotal(freshCart);
    if (finalCents >= min || liftedCents >= min) {
      log(
        `Tier 2 demotion skipped — cart merchandise $${(Math.max(finalCents, liftedCents) / 100).toFixed(2)} still meets $${(min / 100).toFixed(2)}`
      );
      return false;
    }
    log(
      `Tier 2 demotion: final merchandise $${(finalCents / 100).toFixed(2)} below $${(min / 100).toFixed(2)}` +
        (liftedCents !== finalCents ? ` (lifted $${(liftedCents / 100).toFixed(2)})` : '')
    );
    return true;
  }

  function notifyTier2CodeCleared() {
    try {
      document.dispatchEvent(new CustomEvent('free-gift:tier2-code-cleared'));
      document.dispatchEvent(new CustomEvent('free-gift:tier2-code-changed'));
    } catch (_) {}
  }

  function isPaidMainProductLine(item) {
    if (!item || isShippingMerchLine(item)) return false;
    if (!isMainProductComponent(item)) return false;
    if (isTier2GiftLine(item)) return false;
    if (itemIsTierGift(item, '1')) return false;
    return true;
  }

  function getBundleComponentsForParent(parentKey, cart) {
    const pk = String(parentKey || '');
    if (!pk) return [];
    const list = [];
    const seen = new Set();

    const push = (comp) => {
      if (!comp) return;
      const lk = comp.line_key || comp.key || '';
      if (lk && seen.has(lk)) return;
      if (lk) seen.add(lk);
      list.push(comp);
    };

    try {
      const map = window.cartItemComponents;
      if (map && map[pk] && Array.isArray(map[pk].components)) {
        map[pk].components.forEach(push);
      }
    } catch (_) {}

    for (const item of cart?.items || []) {
      if (item?.key === pk && Array.isArray(item.item_components)) {
        item.item_components.forEach(push);
      }
    }

    return list;
  }

  /**
   * Tier 2 gift removal — iterate every bundle / standalone line.
   * Identify gifts via isTier2GiftLine (property-based; never paid same-SKU purchases).
   * Paid mains remain in bundle → remove gift component only.
   * Gift-only bundle → remove entire bundle parent (incl. shipping).
   */
  function collectTier2GiftRemovalPlan(cart) {
    const bundleParents = [];
    const componentLines = [];
    const parentKeysHandled = new Set();
    const lineKeysAdded = new Set();

    const addComponent = (item, reason) => {
      const lk = item.line_key || item.key;
      if (!lk || lineKeysAdded.has(lk)) return;
      lineKeysAdded.add(lk);
      const gid = item && item.properties ? String(item.properties._gid || '') : '';
      componentLines.push({ lineKey: lk, key: item.key, gid });
      log(`Tier 2 removal target (component): ${lk}` + (reason ? ` — ${reason}` : ''));
    };

    const addParent = (parentKey, reason, giftComp) => {
      const pk = String(parentKey || '');
      if (!pk || parentKeysHandled.has(pk)) return;
      parentKeysHandled.add(pk);
      const gid = giftComp && giftComp.properties ? String(giftComp.properties._gid || '') : '';
      bundleParents.push({ lineKey: pk, key: pk, type: 'tier2_gift_only_bundle_parent', gid });
      log(`Tier 2 removal target (bundle parent): ${pk}` + (reason ? ` — ${reason}` : ''));
    };

    // Aggregate every component we can see for each parent BEFORE deciding. Sources disagree:
    // window.cartItemComponents can be stale (missing a paid main), while cart.items[].item_components
    // is fresh. If we decided per-source, a stale "gift-only" reading could win and delete the whole
    // bundle. Merging first lets any protected line (from any source) veto parent removal.
    const componentsByParent = new Map();
    const mergeComponents = (parentKey, components) => {
      const pk = String(parentKey || '');
      if (!pk || !Array.isArray(components) || !components.length) return;
      let bucket = componentsByParent.get(pk);
      if (!bucket) {
        bucket = { list: [], seenKeys: new Set() };
        componentsByParent.set(pk, bucket);
      }
      components.forEach((c) => {
        if (!c) return;
        const ck = String(c.line_key || c.key || '');
        if (ck) {
          if (bucket.seenKeys.has(ck)) return;
          bucket.seenKeys.add(ck);
        }
        bucket.list.push(c);
      });
    };

    try {
      const map = window.cartItemComponents;
      if (map && typeof map === 'object') {
        for (const [parentKey, bundle] of Object.entries(map)) {
          mergeComponents(parentKey, bundle && bundle.components);
        }
      }
    } catch (_) {}

    for (const item of cart?.items || []) {
      if (item?.item_components?.length) {
        mergeComponents(item.key, item.item_components);
      }
    }

    if (Array.isArray(window.cartWithComp)) {
      for (const row of window.cartWithComp) {
        const parentKey = row?.cart?.key || row?.key;
        if (parentKey && Array.isArray(row.components)) {
          mergeComponents(parentKey, row.components);
        }
      }
    }

    for (const [parentKey, bucket] of componentsByParent.entries()) {
      const components = bucket.list;
      const gifts = components.filter((c) => c && isTier2GiftLine(c));
      if (!gifts.length) continue;

      // User rule: other `_product: true` mains → gift component only (keep shipping).
      // Gift-only (no other mains) → remove whole parent. Safety: any non-gift/non-shipping
      // sibling (CT-stripped paid) also forces component-only removal.
      if (
        bundleHasOtherMainProductsBesideGifts(components, '2') ||
        bundleHasProtectedMainBesideGifts(components, '2')
      ) {
        gifts.forEach((g) =>
          addComponent(
            g,
            `bundle ${parentKey} has other main/protected line(s) — remove gift component only`
          )
        );
      } else {
        addParent(parentKey, 'gift-only bundle (no other _product mains)', gifts[0]);
      }
    }

    // Standalone Tier 2 gift lines (not already covered as a parent / component)
    const allItems = mergeCartData(cart);
    const bundleParentKeys = new Set(
      typeof window.cartItemComponents === 'object' && window.cartItemComponents !== null
        ? Object.keys(window.cartItemComponents)
        : []
    );

    for (const item of allItems) {
      if (!isTier2GiftLine(item)) continue;
      if (item._isComponent && item._parentKey) {
        if (parentKeysHandled.has(String(item._parentKey))) continue;
        if (lineKeysAdded.has(item.line_key || item.key)) continue;
        addComponent(item, 'component fallback');
        continue;
      }
      if (!item.key || bundleParentKeys.has(item.key) || parentKeysHandled.has(String(item.key))) {
        continue;
      }
      if (lineKeysAdded.has(item.line_key || item.key)) continue;
      addComponent(item, 'standalone gift');
    }

    log(
      `Tier 2 removal plan: ${bundleParents.length} gift-only parent(s), ${componentLines.length} gift line(s)`
    );
    return { bundleParents, componentLines };
  }

  function isMainProductComponent(comp) {
    return getProp(comp, '_product') === 'true' || getProp(comp, '_product') === true;
  }

  /**
   * Paid / other main merchandise beside a tier gift: `_product: true`, not shipping/fee/ribbon,
   * and not the tier gift itself (gifts also carry `_product: true`).
   */
  function isOtherMainProductBesideTierGift(comp, tierStr) {
    if (!comp) return false;
    if (isShippingMerchLine(comp)) return false;
    const t = String(tierStr);
    const isGift = t === '2' ? isTier2GiftLine(comp) : itemIsTierGift(comp, t);
    if (isGift) return false;
    return isMainProductComponent(comp);
  }

  function countOtherMainProductsInComponents(components, tierStr) {
    let n = 0;
    (components || []).forEach((c) => {
      if (isOtherMainProductBesideTierGift(c, tierStr)) n += 1;
    });
    return n;
  }

  /**
   * Part J — protected sibling = any line that is NOT this-tier gift and NOT shipping/fee/ribbon.
   * Includes paid mains (`_product: true`) and any non-gift merch (props may be incomplete after CT).
   * Never treat "same variant ID as gift SKU" as a gift — gift ID is property-based only.
   * Used as a safety net so we never qty=0 a CT parent that still holds non-gift merchandise.
   */
  function bundleHasProtectedMainBesideGifts(components, tierStr) {
    const t = String(tierStr);
    const isGift =
      t === '2' ? (c) => isTier2GiftLine(c) : (c) => itemIsTierGift(c, t);
    const gifts = (components || []).filter((c) => c && isGift(c));
    const giftKeys = new Set(
      gifts.map((g) => String(g.line_key || g.key || '')).filter(Boolean)
    );
    return (components || []).some((c) => {
      if (!c) return false;
      const ck = String(c.line_key || c.key || '');
      if (ck && giftKeys.has(ck)) return false;
      if (isGift(c)) return false;
      if (isShippingMerchLine(c)) return false;
      return true;
    });
  }

  /** User rule: other `_product: true` mains (excluding this-tier gift + shipping) remain in bundle. */
  function bundleHasOtherMainProductsBesideGifts(components, tierStr) {
    return countOtherMainProductsInComponents(components, tierStr) > 0;
  }

  function isFeeOrRibbonLine(item) {
    if (!item) return false;
    return (
      getProp(item, '_fee') === 'true' ||
      getProp(item, '_fee') === true ||
      getProp(item, '_ribbon') === 'true' ||
      getProp(item, '_ribbon') === true
    );
  }

  /** Collect every line/component in cart (+ cartItemComponents) that shares `_gid`. */
  function collectLinesForGid(cart, gidStr) {
    const g = String(gidStr || '').trim();
    const out = [];
    const seen = new Set();
    if (!g) return out;

    const push = (item) => {
      if (!item) return;
      if (String(getProp(item, '_gid') || '').trim() !== g) return;
      const k = String(item.line_key || item.key || '');
      if (k && seen.has(k)) return;
      if (k) seen.add(k);
      out.push(item);
    };

    for (const item of (cart && cart.items) || []) {
      push(item);
      if (Array.isArray(item.item_components)) item.item_components.forEach(push);
    }
    try {
      const map = window.cartItemComponents;
      if (map && typeof map === 'object') {
        for (const [parentKey, bundle] of Object.entries(map)) {
          if (!bundle) continue;
          if (bundle.gid != null && String(bundle.gid) === g) {
            // Include opaque shipping-only parent key for removal.
            const parentItem = ((cart && cart.items) || []).find(
              (it) => String(it.key) === String(parentKey)
            );
            if (parentItem) push(Object.assign({}, parentItem, {
              properties: Object.assign(
                { _gid: g },
                propsToObject(parentItem.properties)
              ),
            }));
          }
          if (Array.isArray(bundle.components)) bundle.components.forEach(push);
        }
      }
    } catch (_) {}
    return out;
  }

  function countOtherMainProductsForGid(cart, gidStr, tierStr) {
    return countOtherMainProductsInComponents(collectLinesForGid(cart, gidStr), tierStr);
  }

  function gidHasRemainingGiftMerchandise(cart, gidStr, tierStr) {
    const lines = collectLinesForGid(cart, gidStr);
    const t = String(tierStr || '');
    return lines.some((line) => {
      if (!line) return false;
      if (isShippingMerchLine(line) || isFeeOrRibbonLine(line)) return false;
      if (t === '2') return isTier2GiftLine(line);
      if (t === '1') return itemIsTierGift(line, '1');
      return itemIsTierGift(line, '1') || isTier2GiftLine(line);
    });
  }

  function giftLineGidString(item) {
    try {
      const g = getProp(item, '_gid');
      return g != null ? String(g).trim() : '';
    } catch (_) {
      return '';
    }
  }

  /**
   * GIFT-ORPHAN-SHIP-CLEANUP-2026-09-15: after gift merch is gone from a `gift_*` GID with no
   * other `_product` mains, remove detached shipping/fee/ribbon siblings for that GID.
   *
   * Parent-scoped cleanup alone misses the common CT shape where gift merch and shipping are
   * separate top-level lines sharing `_gid: gift_…`. Safe only for `gift_`-prefixed GIDs —
   * paid shared GIDs must not be wiped cart-wide (2026-07-20 regression).
   */
  async function removeOrphanGiftGidShippingWhenNoMains(
    changeQtyFn,
    cart,
    gidSet,
    removedTierStr
  ) {
    let mutated = false;
    let fresh = cart || (await getFreshCart());
    const gids = Array.from(gidSet || [])
      .map((g) => String(g || '').trim())
      .filter((g) => g && g.startsWith('gift_'));
    if (!gids.length) return { mutated, cart: fresh };

    for (const g of gids) {
      if (countOtherMainProductsForGid(fresh, g, removedTierStr) > 0) {
        log(
          `[Part J] Keep gift_ shipping for ${g} — other _product main(s) remain on GID`
        );
        continue;
      }
      if (gidHasRemainingGiftMerchandise(fresh, g, removedTierStr)) {
        log(`[Part J] Keep gift_ shipping for ${g} — gift merchandise still present`);
        continue;
      }

      const lines = collectLinesForGid(fresh, g);
      const toRemove = [];
      const seen = new Set();
      lines.forEach((line) => {
        if (!line) return;
        const key = String(line.line_key || line.key || '');
        if (!key || seen.has(key)) return;
        if (isShippingMerchLine(line) || isFeeOrRibbonLine(line)) {
          seen.add(key);
          toRemove.push({ key, reason: 'orphan gift_ shipping/fee/ribbon' });
        }
      });

      try {
        if (typeof window.multishipClearPendingRecipientShipProps === 'function') {
          window.multishipClearPendingRecipientShipProps(g);
        }
      } catch (_) {}
      try {
        clearMultishipTier2AssignStash(g);
      } catch (_) {}

      if (!toRemove.length) {
        log(`[Part J] gift_ ${g}: no orphan shipping shell left to remove`);
        continue;
      }

      for (const entry of toRemove) {
        log(
          `[Part J] gift_ ${g}: no gift/mains left — removing ${entry.reason} id=${entry.key}`
        );
        const ok = await changeQtyFn(entry.key, 0);
        if (ok) mutated = true;
        await pauseGiftCart(280);
      }
    }

    if (mutated) fresh = await getFreshCart();
    return { mutated, cart: fresh };
  }

  /**
   * After removing a tier gift from one bundle: if no other `_product: true` mains remain
   * **in that parent bundle only**, remove shipping/fee/ribbon for that bundle.
   * Never use cart-wide `_gid` — unrelated bundles can share a GID after CT / upgrades.

  /**
   * After removing a tier gift from one bundle: if no other `_product: true` mains remain
   * **in that parent bundle only**, remove shipping/fee/ribbon for that bundle.
   * Never use cart-wide `_gid` — unrelated bundles can share a GID after CT / upgrades.
   */
  async function removeOrphanShippingForBundleIfNoOtherMains(
    changeQtyFn,
    cart,
    parentKey,
    removedTierStr
  ) {
    let mutated = false;
    let fresh = cart || (await getFreshCart());
    const pk = String(parentKey || '').trim();
    if (!pk) return { mutated, cart: fresh };

    const comps = getBundleComponentsForParent(pk, fresh);
    const otherMains = countOtherMainProductsInComponents(comps, removedTierStr);
    if (otherMains > 0) {
      log(
        `[Part J] Keep shipping for bundle ${pk.slice(0, 24)}… — ${otherMains} other _product main(s) in bundle`
      );
      return { mutated, cart: fresh };
    }

    const toRemove = [];
    const seen = new Set();

    comps.forEach((line) => {
      if (!line) return;
      const key = String(line.line_key || line.key || '');
      if (!key || seen.has(key)) return;
      if (isOtherMainProductBesideTierGift(line, removedTierStr)) return;
      if (isShippingMerchLine(line) || isFeeOrRibbonLine(line)) {
        seen.add(key);
        toRemove.push({ key, reason: 'shipping/fee/ribbon in bundle' });
      }
    });

    // Opaque shipping-only parent with no components left after gift removal.
    if (!comps.length) {
      try {
        const map = window.cartItemComponents;
        const bundle = map && map[pk];
        const bundleComps = bundle && Array.isArray(bundle.components) ? bundle.components : [];
        const hasOtherMain = countOtherMainProductsInComponents(bundleComps, removedTierStr) > 0;
        if (!hasOtherMain) {
          const parentItem = (fresh.items || []).find((it) => String(it.key) === pk);
          const title = (bundle && bundle.title) || (parentItem && parentItem.title) || '';
          const looksLikeShippingShell =
            !bundleComps.length ||
            bundleComps.every(
              (c) =>
                !c ||
                isShippingMerchLine(c) ||
                isFeeOrRibbonLine(c) ||
                itemIsTierGift(c, removedTierStr)
            );
          if (parentItem && looksLikeShippingShell && !seen.has(pk)) {
            seen.add(pk);
            toRemove.push({ key: pk, reason: 'empty shipping-only parent' });
          } else if (
            parentItem &&
            String(title).toLowerCase().indexOf('shipping') !== -1 &&
            !seen.has(pk)
          ) {
            seen.add(pk);
            toRemove.push({ key: pk, reason: 'shipping-only parent shell' });
          }
        }
      } catch (_) {}
    }

    if (!toRemove.length) {
      log(`[Part J] Bundle ${pk.slice(0, 24)}…: no shipping shell to remove`);
      return { mutated, cart: fresh };
    }

    for (const entry of toRemove) {
      log(
        `[Part J] Bundle ${pk.slice(0, 24)}…: no other _product mains — removing ${entry.reason} id=${entry.key}`
      );
      const ok = await changeQtyFn(entry.key, 0);
      if (ok) mutated = true;
      await pauseGiftCart(280);
    }
    if (mutated) fresh = await getFreshCart();
    return { mutated, cart: fresh };
  }

  async function removeOrphanShippingForAffectedBundles(
    changeQtyFn,
    cart,
    parentKeySet,
    removedTierStr
  ) {
    let mutated = false;
    let fresh = cart;
    const keys = Array.from(parentKeySet || []).filter((k) => k && String(k).trim() !== '');
    if (!keys.length) return { mutated, cart: fresh };

    for (const pk of keys) {
      const r = await removeOrphanShippingForBundleIfNoOtherMains(
        changeQtyFn,
        fresh,
        pk,
        removedTierStr
      );
      if (r.mutated) mutated = true;
      fresh = r.cart || fresh;
    }
    return { mutated, cart: fresh };
  }

  /** @deprecated Use bundle-scoped cleanup — gid-wide removal hits unrelated bundles. */
  async function removeOrphanShippingWhenNoOtherMains(
    changeQtyFn,
    cart,
    gidSet,
    removedTierStr
  ) {
    // Legacy callers pass gid set — map to parent keys for those gids only when unambiguous.
    const parentKeys = new Set();
    try {
      const map = window.cartItemComponents;
      if (map && typeof map === 'object') {
        const gids = Array.from(gidSet || []);
        for (const [pk, bundle] of Object.entries(map)) {
          if (!bundle) continue;
          const bg = bundle.gid != null ? String(bundle.gid) : '';
          if (bg && gids.indexOf(bg) !== -1) parentKeys.add(String(pk));
        }
      }
    } catch (_) {}
    if (!parentKeys.size) return { mutated: false, cart: cart };
    return removeOrphanShippingForAffectedBundles(
      changeQtyFn,
      cart,
      parentKeys,
      removedTierStr
    );
  }

  /**
   * Part J shared executor: remove gifts of `tierStr` as component-only when the bundle still
   * has protected main products; otherwise remove the gift-only parent whole.
   * @param {(id: string, qty: number) => Promise<boolean>} changeQtyFn
   * @returns {Promise<{ mutated: boolean, cart: object }>}
   */
  async function removeGiftsRespectingBundleShape(changeQtyFn, cart, tierStr) {
    let mutated = false;
    let cartForRemoval = cart;
    const t = String(tierStr);
    const affectedParentKeys = new Set();
    // gift_* GIDs whose merch we removed — used for detached sibling shipping cleanup.
    const removedGiftGids = new Set();
    const trackGiftGid = (gidLike) => {
      const g = String(gidLike || '').trim();
      if (g && g.startsWith('gift_')) removedGiftGids.add(g);
    };

    if (t === '2') {
      let passes = 0;
      while (passes < 3) {
        passes += 1;
        const plan = collectTier2GiftRemovalPlan(cartForRemoval);
        const planned = plan.bundleParents.length + plan.componentLines.length;
        if (!planned) {
          if (passes === 1) log('No Tier 2 gift lines found across bundles');
          break;
        }

        for (const entry of plan.bundleParents) {
          const id = entry.lineKey || entry.key;
          if (!id) continue;
          trackGiftGid(entry.gid);
          if (!bundleParentIsGiftOnlyInFreshCart(cartForRemoval, id, '2')) {
            log(
              `[Part J] Tier 2 parent ${id} has protected content — gift component(s) only`
            );
            affectedParentKeys.add(String(id));
            const parentLine = (cartForRemoval.items || []).find(
              (it) => String(it.key) === String(id)
            );
            let giftComps =
              parentLine && Array.isArray(parentLine.item_components)
                ? parentLine.item_components.filter((c) => c && isTier2GiftLine(c))
                : [];
            if (!giftComps.length) {
              try {
                const map = window.cartItemComponents;
                const bundle = map && map[id];
                if (bundle && Array.isArray(bundle.components)) {
                  giftComps = bundle.components.filter((c) => c && isTier2GiftLine(c));
                }
              } catch (_) {}
            }
            for (const g of giftComps) {
              const compId = g.line_key || g.key;
              if (!compId) continue;
              trackGiftGid(giftLineGidString(g));
              const okc = await changeQtyFn(compId, 0);
              if (okc) {
                mutated = true;
                clearMultishipTier2AssignStash(g.properties && g.properties._gid);
              }
              await pauseGiftCart(280);
            }
            continue;
          }
          log(`[Part J] Removing Tier 2 gift-only bundle parent id=${id}`);
          const ok = await changeQtyFn(id, 0);
          if (ok) {
            mutated = true;
            clearMultishipTier2AssignStash(entry.gid);
          }
          await pauseGiftCart(320);
        }
        for (const entry of plan.componentLines) {
          const id = entry.lineKey || entry.key;
          if (!id) continue;
          trackGiftGid(entry.gid);
          // Resolve parent for bundle-scoped shipping cleanup.
          try {
            const all = mergeCartData(cartForRemoval);
            const hit = all.find(
              (it) =>
                it &&
                it._isComponent &&
                (String(it.line_key || '') === String(id) || String(it.key || '') === String(id))
            );
            if (hit && hit._parentKey) affectedParentKeys.add(String(hit._parentKey));
            if (hit) trackGiftGid(giftLineGidString(hit));
          } catch (_) {}
          log(`[Part J] Removing Tier 2 gift component id=${id}`);
          const ok = await changeQtyFn(id, 0);
          if (ok) {
            mutated = true;
            clearMultishipTier2AssignStash(entry.gid);
          }
          await pauseGiftCart(280);
        }

        cartForRemoval = await getFreshCart();
        if (giftExistsInCart('2', cartForRemoval)) {
          log(`Tier 2 gift still present after pass ${passes} — retrying`);
          continue;
        }
        break;
      }

      const shipCleanup2 = await removeOrphanShippingForAffectedBundles(
        changeQtyFn,
        cartForRemoval,
        affectedParentKeys,
        '2'
      );
      if (shipCleanup2.mutated) mutated = true;
      cartForRemoval = shipCleanup2.cart || cartForRemoval;
      // Detached gift_ shipping siblings (same GID, different line key) survive parent wipe.
      const giftGidShip2 = await removeOrphanGiftGidShippingWhenNoMains(
        changeQtyFn,
        cartForRemoval,
        removedGiftGids,
        '2'
      );
      if (giftGidShip2.mutated) mutated = true;
      cartForRemoval = giftGidShip2.cart || cartForRemoval;
      return { mutated, cart: cartForRemoval };
    }

    // Tier 1 — only bundles that contain a confirmed Tier 1 gift line.
    const giftOnlyBundles = collectGiftOnlyBundleParentsToRemove(cartForRemoval, '1');
    for (const entry of giftOnlyBundles) {
      const id = entry.lineKey || entry.key;
      if (!id) continue;
      trackGiftGid(entry.gid);
      if (!entry.gid) {
        try {
          const comps = getBundleComponentsForParent(id, cartForRemoval);
          const giftComp = (comps || []).find((c) => c && itemIsTierGift(c, '1'));
          trackGiftGid(giftLineGidString(giftComp));
        } catch (_) {}
      }

      if (!bundleParentIsGiftOnlyInFreshCart(cartForRemoval, id, '1')) {
        log(
          `[Part J] Tier 1 parent ${id} has other content — gift component(s) only` +
            (bundleHasOtherMainProductsBesideGifts(
              getBundleComponentsForParent(id, cartForRemoval),
              '1'
            )
              ? ' (other _product mains present — keep shipping)'
              : ' (no other _product mains — shipping cleaned after)')
        );
        affectedParentKeys.add(String(id));
        const parentLine = (cartForRemoval.items || []).find(
          (it) => String(it.key) === String(id)
        );
        let giftComps =
          parentLine && Array.isArray(parentLine.item_components)
            ? parentLine.item_components.filter((c) => c && itemIsTierGift(c, '1'))
            : [];
        if (!giftComps.length) {
          try {
            const map = window.cartItemComponents;
            const bundle = map && map[id];
            if (bundle && Array.isArray(bundle.components)) {
              giftComps = bundle.components.filter((c) => c && itemIsTierGift(c, '1'));
            }
          } catch (_) {}
        }
        for (const g of giftComps) {
          const compId = g.line_key || g.key;
          if (!compId) continue;
          trackGiftGid(giftLineGidString(g));
          const okc = await changeQtyFn(compId, 0);
          if (okc) mutated = true;
          await pauseGiftCart(280);
        }
        continue;
      }
      log(
        `[Part J] Removing Tier 1 gift-only bundle parent id=${id} (no other _product mains)`
      );
      const ok = await changeQtyFn(id, 0);
      if (ok) mutated = true;
      await pauseGiftCart(320);
    }
    if (giftOnlyBundles.length) {
      cartForRemoval = await getFreshCart();
    }

    const toRemove = collectGiftsToRemove(cartForRemoval, null);
    for (const entry of toRemove) {
      const id = entry.lineKey || entry.key;
      if (!id) continue;
      try {
        const all = mergeCartData(cartForRemoval);
        const hit = all.find(
          (it) =>
            it &&
            (String(it.line_key || '') === String(id) || String(it.key || '') === String(id))
        );
        if (hit && hit._parentKey) {
          affectedParentKeys.add(String(hit._parentKey));
        } else if (hit && hit.key && !hit._isComponent) {
          affectedParentKeys.add(String(hit.key));
        }
        if (hit) trackGiftGid(giftLineGidString(hit));
      } catch (_) {}
      log(`[Part J] Removing Tier 1 gift line id=${id}`);
      const ok = await changeQtyFn(id, 0);
      if (ok) mutated = true;
      await pauseGiftCart(280);
    }
    if (toRemove.length) {
      cartForRemoval = await getFreshCart();
    }

    const shipCleanup1 = await removeOrphanShippingForAffectedBundles(
      changeQtyFn,
      cartForRemoval,
      affectedParentKeys,
      '1'
    );
    if (shipCleanup1.mutated) mutated = true;
    cartForRemoval = shipCleanup1.cart || cartForRemoval;
    const giftGidShip1 = await removeOrphanGiftGidShippingWhenNoMains(
      changeQtyFn,
      cartForRemoval,
      removedGiftGids,
      '1'
    );
    if (giftGidShip1.mutated) mutated = true;
    cartForRemoval = giftGidShip1.cart || cartForRemoval;

    return { mutated, cart: cartForRemoval };
  }

  /**
   * Execution-time safety net: confirm against the FRESH cart that a bundle parent truly holds only
   * gift + shipping/fee/ribbon lines before we remove the whole parent. If the parent isn't found,
   * has no visible components, or holds any protected (non-gift, non-shipping) line, we refuse to
   * remove the parent — the caller should remove only the gift component instead. This guarantees a
   * regular product is never deleted just because it shares a variant with a gift, or because its
   * identity props were transiently dropped.
   */
  function bundleParentIsGiftOnlyInFreshCart(cart, parentKey, tierStr) {
    const pk = String(parentKey || '');
    if (!pk || !cart || !Array.isArray(cart.items)) return false;
    const parent = cart.items.find((it) => String(it.key) === pk);
    if (!parent) return false;
    let comps = Array.isArray(parent.item_components) ? parent.item_components : [];
    if (!comps.length) {
      try {
        const map = window.cartItemComponents;
        const bundle = map && map[pk];
        if (bundle && Array.isArray(bundle.components) && bundle.components.length) {
          comps = bundle.components;
        }
      } catch (_) {}
    }
    if (!comps.length) return false;
    const isGift =
      String(tierStr) === '2'
        ? (c) => isTier2GiftLine(c)
        : (c) => itemIsTierGift(c, String(tierStr));
    const gifts = comps.filter((c) => c && isGift(c));
    if (!gifts.length) return false;
    // User rule: gift-only when no other `_product: true` mains remain beside the gift.
    if (bundleHasOtherMainProductsBesideGifts(comps, tierStr)) return false;
    // Safety: never wipe a parent that still holds non-gift / non-shipping merch
    // (e.g. CT-stripped paid line missing `_product` briefly).
    if (bundleHasProtectedMainBesideGifts(comps, tierStr)) return false;
    return true;
  }

  /**
   * Gift-only bundles (no other `_product: true` mains) — remove parent line so the whole
   * bundle (gift + shipping) goes away.
   */
  function collectGiftOnlyBundleParentsToRemove(cart, removeTier) {
    const list = [];
    const removeTierStr = String(removeTier);
    const addedParents = new Set();

    // Aggregate components per parent across sources before deciding (see collectTier2GiftRemovalPlan
    // for rationale) so a stale source can never win a destructive "gift-only" decision.
    const componentsByParent = new Map();
    const mergeComponents = (parentKey, components) => {
      const pk = String(parentKey || '');
      if (!pk || !Array.isArray(components) || !components.length) return;
      let bucket = componentsByParent.get(pk);
      if (!bucket) {
        bucket = { list: [], seenKeys: new Set() };
        componentsByParent.set(pk, bucket);
      }
      components.forEach((c) => {
        if (!c) return;
        const ck = String(c.line_key || c.key || '');
        if (ck) {
          if (bucket.seenKeys.has(ck)) return;
          bucket.seenKeys.add(ck);
        }
        bucket.list.push(c);
      });
    };

    try {
      const map = window.cartItemComponents;
      if (map && typeof map === 'object') {
        for (const [parentKey, bundle] of Object.entries(map)) {
          mergeComponents(parentKey, bundle && bundle.components);
        }
      }
    } catch (_) {}

    for (const item of cart?.items || []) {
      if (item?.item_components?.length) {
        mergeComponents(item.key, item.item_components);
      }
    }

    if (Array.isArray(window.cartWithComp)) {
      for (const row of window.cartWithComp) {
        const parentKey = row?.cart?.key || row?.key;
        if (parentKey && Array.isArray(row.components)) {
          mergeComponents(parentKey, row.components);
        }
      }
    }

    for (const [parentKey, bucket] of componentsByParent.entries()) {
      if (addedParents.has(parentKey)) continue;
      const components = bucket.list;
      const tierGifts = components.filter((c) => c && itemIsTierGift(c, removeTierStr));
      if (!tierGifts.length) continue;

      // Keep parent when other `_product: true` mains share the bundle (shipping stays).
      if (bundleHasOtherMainProductsBesideGifts(components, removeTierStr)) continue;
      // Safety net: non-gift / non-shipping sibling without `_product` still blocks parent wipe.
      if (bundleHasProtectedMainBesideGifts(components, removeTierStr)) continue;

      addedParents.add(parentKey);
      const giftGid = giftLineGidString(tierGifts[0]);
      list.push({
        lineKey: parentKey,
        key: parentKey,
        type: 'gift_only_bundle_parent',
        gid: giftGid,
      });
      log(
        `Gift-only tier ${removeTierStr} bundle parent to remove: ${parentKey} (no other _product mains)` +
          (giftGid ? ` gid=${giftGid}` : '')
      );
    }

    return list;
  }

  function collectGiftsToRemove(cart, keepTier) {
    const list = [];
    const allItems = mergeCartData(cart);
    const addedLineKeys = new Set();
    const bundleParentKeys = new Set(
      typeof window.cartItemComponents === 'object' && window.cartItemComponents !== null
        ? Object.keys(window.cartItemComponents)
        : []
    );

    const pushRemoval = (lineKey, key) => {
      const lk = lineKey || key;
      if (!lk || addedLineKeys.has(String(lk))) return;
      addedLineKeys.add(String(lk));
      list.push({ lineKey: lk, key: key || lk });
    };

    for (const item of allItems) {
      if (!item || isShippingMerchLine(item)) continue;

      const isT2 = isTier2GiftLine(item);
      const isT1 = !isT2 && itemIsTierGift(item, '1');
      if (!isT1 && !isT2) continue;

      // Tier 2 lines are removed by collectTier2GiftRemovalPlan when tier 2 is inactive.
      if (isT2) continue;

      if (keepTier === '1' && isT1) continue;

      if (item._isComponent && item._parentKey) {
        pushRemoval(item.line_key || item.key, item.key);
      } else if (!item._isComponent && item.key) {
        if (bundleParentKeys.has(item.key)) continue;
        pushRemoval(item.line_key || item.key, item.key);
      }
    }

    // Explicit cartItemComponents pass — catches Tier 1 gifts merged into paid-bundle parents
    // when mergeCartData missed nested component keys after CT lag.
    if (keepTier !== '1') {
      try {
        const map = window.cartItemComponents;
        if (map && typeof map === 'object') {
          for (const bundle of Object.values(map)) {
            if (!bundle || !Array.isArray(bundle.components)) continue;
            for (const comp of bundle.components) {
              if (!comp || isTier2GiftLine(comp)) continue;
              if (!itemIsTierGift(comp, '1')) continue;
              pushRemoval(comp.line_key || comp.key, comp.key);
            }
          }
        }
      } catch (_) {}

      // UNDER-THRESHOLD-GIFT-REMOVE-2026-09-15: assigned-into-existing-bundle gifts often lose
      // gift props after CT; still remove $0 configured Tier 1 SKUs found as components.
      try {
        const ids = getTierVariantIds('1');
        const consider = (comp) => {
          if (!comp || isShippingMerchLine(comp) || isTier2GiftLine(comp)) return;
          if (itemIsTierGift(comp, '1')) {
            pushRemoval(comp.line_key || comp.key, comp.key);
            return;
          }
          const vid =
            comp.variant_id != null
              ? String(comp.variant_id)
              : comp.id != null
                ? String(comp.id)
                : '';
          if (!vid || !ids.has(vid)) return;
          if (!isZeroPriceLine(comp)) return;
          const lk = comp.line_key || comp.key;
          if (!lk || bundleParentKeys.has(String(lk))) return;
          pushRemoval(lk, comp.key);
        };
        for (const item of (cart && cart.items) || []) {
          if (!item) continue;
          if (Array.isArray(item.item_components)) item.item_components.forEach(consider);
          // Standalone $0 gift line (not a CT parent).
          if (!bundleParentKeys.has(String(item.key || ''))) consider(item);
        }
        const map = window.cartItemComponents;
        if (map && typeof map === 'object') {
          for (const [pk, bundle] of Object.entries(map)) {
            if (!bundle || !Array.isArray(bundle.components)) continue;
            for (const comp of bundle.components) {
              if (!comp) continue;
              // Never queue the parent key itself — that would wipe paid merch.
              const lk = String(comp.line_key || comp.key || '');
              if (lk && lk === String(pk)) continue;
              consider(comp);
            }
          }
        }
      } catch (_) {}
    }

    return list;
  }

  function pause(ms) {
    return new Promise((r) => setTimeout(r, ms));
  }

  function isFreeGiftOnShippingPage() {
    try {
      return !!document.querySelector('shipping-page');
    } catch (_) {
      return false;
    }
  }

  /** Shipping page keeps Cart Transform settle pauses; cart drawer / PDP stay snappy. */
  function pauseGiftCart(ms) {
    if (isFreeGiftOnShippingPage()) return pause(ms);
    return Promise.resolve();
  }

  function getGiftCartSectionPayload() {
    const fallback = ['cart-drawer', 'cart-icon-bubble'];
    try {
      const cart =
        document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      if (cart && typeof cart.getSectionsToRender === 'function') {
        const ids = cart
          .getSectionsToRender()
          .map((s) => s.section || s.id)
          .filter(Boolean);
        if (ids.length) {
          return { sections: ids.join(','), sections_url: window.location.pathname };
        }
      }
    } catch (_) {}
    return { sections: fallback.join(','), sections_url: window.location.pathname };
  }

  function applyGiftCartSections(parsed) {
    if (!parsed || !parsed.sections) return false;
    try {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(parsed, { openDrawer: false });
        if (parsed.sections['cart-icon-bubble'] && typeof window.syncCartIconBubble === 'function') {
          window.syncCartIconBubble(parsed.sections['cart-icon-bubble']);
        }
        return true;
      }
      const note = document.querySelector('cart-notification');
      if (note && typeof note.renderContents === 'function') {
        note.renderContents(parsed);
        return true;
      }
    } catch (_) {}
    return false;
  }

  class FreeGiftManager {
    constructor() {
      this.settings = getSettings();
      this.processing = false;
      this.debounceTimer = null;
      this._externalCartTimer = null;
      this._plpDeferredTimer = null;
      this._shippingSectionRefreshTimer = null;
      this.cooldownUntil = 0;
      this._loaderShownViaShippingPage = false;
      this._loaderOwnedByFreeGift = false;
      this._syncInFlight = null;
      this._settleRetryTimer = null;
      this._settleRetryAttempts = 0;
      this._syncQueued = false;
      this._appliedDrawerFromApi = false;

      window.freeGiftManager = this;
      this._init();
    }

    async _init() {
      if (!isFeatureEnabled()) {
        log('Feature disabled (free_gift_enabled and promo_gift_enabled are both off)');
        return;
      }

      captureTier2CodeFromLanding();

      if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (!event || event.source === 'free-gift') return;
          this.notifyCartChanged(event);
        });
      }

      document.addEventListener('cart:updated', (ev) => {
        const detail = (ev && ev.detail) || {};
        if (detail.source === 'free-gift') return;
        this.notifyCartChanged(detail);
      });

      document.addEventListener('theme:cart-drawer:open', () => {
        this.syncGift();
      });

      document.addEventListener('free-gift:tier2-code-changed', () => {
        // Ignore while a sync is already assessing/mutating — prevents loader flash loops
        // when promote/demote fires the same event from inside evaluateTierAndSync.
        if (this.processing || this._syncInFlight) {
          return;
        }
        this.syncGiftForced();
      });

      const runInitial = () => this.syncGift();
      if (this._isOnShippingPage()) {
        // Give MultiShip time to hydrate cartItemComponents before the first gift sync.
        // Running immediately after Save-refresh caused false "gift missing" → re-add without shipping.
        setTimeout(() => {
          this._scheduleSettleRetry(400, 'shipping page initial sync');
        }, 1200);
      } else if (document.readyState === 'complete') {
        runInitial();
      } else {
        window.addEventListener('load', runInitial);
      }
    }

    _isOnShippingPage() {
      try {
        return !!document.querySelector('shipping-page');
      } catch (_) {
        return false;
      }
    }

    _dollarsToCents(d) {
      const n = parseFloat(String(d).replace(/[^\d.]/g, ''));
      return isNaN(n) || n <= 0 ? null : Math.round(n * 100);
    }

    notifyCartChanged(event) {
      if (!event || event.source === 'free-gift') return;
      if (event.source === 'collection-quick-add') {
        this.schedulePlpDeferredSync(event);
        return;
      }
      this.scheduleExternalCartSync(event);
    }

    scheduleExternalCartSync(event) {
      this._pendingExternalCartEvent = event || {};
      // Do NOT show the shipping overlay here — every cart:updated / Save / section refresh
      // used to flash "Updating your free gift…" even when the gift already matched.
      // evaluateTierAndSync shows the loader only when a cart mutation is required.
      clearTimeout(this._externalCartTimer);
      const delay = this._isOnShippingPage() ? 600 : 80;
      this._externalCartTimer = setTimeout(() => {
        this._externalCartTimer = null;
        void this._runDeferredGiftSync(this._pendingExternalCartEvent || {}, 'Cart updated');
        this._pendingExternalCartEvent = null;
      }, delay);
    }

    schedulePlpDeferredSync(event) {
      clearTimeout(this._plpDeferredTimer);
      const until = window.__plpQtyMutationUntil || 0;
      const delay = Math.max(900, until - Date.now() + 250);
      this._plpDeferredTimer = setTimeout(() => {
        this._plpDeferredTimer = null;
        void this._runDeferredGiftSync(event, 'PLP cart settled');
      }, delay);
    }

    syncGift() {
      clearTimeout(this.debounceTimer);
      const debounceMs = this._isOnShippingPage() ? 650 : 80;
      // Silent schedule on shipping page — overlay only if evaluateTierAndSync must mutate.
      this.debounceTimer = setTimeout(() => {
        this.debounceTimer = null;
        void this.evaluateTierAndSync();
      }, debounceMs);
    }

    syncGiftForced() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
      this.cooldownUntil = 0;
      // User-driven (Tier 2 code change) — keep visible feedback.
      this._beginShippingPageSyncUi('Updating your free gift…');
      return this.evaluateTierAndSync({ forceLoader: true });
    }

    async _runDeferredGiftSync(event, label) {
      if (isMultishipCartSettling()) {
        this._scheduleSettleRetry(2000, `${label} (settle defer)`);
        return;
      }
      if (isMultiPackPricePreviewActive()) {
        this._scheduleSettleRetry(600, `${label} (multi-pack price preview)`);
        return;
      }
      if (window.__plpQtyMutationUntil && Date.now() < window.__plpQtyMutationUntil) {
        this.schedulePlpDeferredSync(event);
        return;
      }
      if (this.processing || this._syncInFlight) {
        this._syncQueued = true;
        this._retryAfterDelay(this._isOnShippingPage() ? 700 : 80);
        return;
      }
      log(`${label} — running evaluateTierAndSync`);
      await this.evaluateTierAndSync();
    }

    _beginShippingPageSyncUi(message) {
      // While Save/rebuild is mutating, do not arm pending or the overlay — that blocked
      // hideShippingPageLoader (busy guard) and left the spinner up until the watchdog.
      if (isMultishipCartSettling()) {
        return;
      }
      try {
        window.__freeGiftSyncPending = true;
      } catch (_) {}
      if (this._isOnShippingPage()) {
        this._showLoader(message != null ? message : 'Updating your free gift…');
      }
    }

    _releaseShippingPageSyncUiIfIdle() {
      if (this.processing || this.debounceTimer || this._externalCartTimer || this._settleRetryTimer) {
        return;
      }
      try {
        window.__freeGiftSyncPending = false;
      } catch (_) {}
      this._setShippingProductInteractionsBlocked(false);
      if (this._loaderShownViaShippingPage || this._loaderOwnedByFreeGift) {
        this._hideLoader();
      }
    }

    /**
     * Defer free-gift sync while multi-ship is mutating — but always clear pending + hide our
     * loader first so the shipping overlay cannot stick for the whole settle window.
     */
    _scheduleSettleRetry(ms, reason) {
      const delay = Math.max(400, Number(ms) || 2000);
      this._settleRetryAttempts = (this._settleRetryAttempts || 0) + 1;
      if (this._settleRetryAttempts > 12) {
        log(
          `Settle retry cap reached (${this._settleRetryAttempts}) — releasing loader` +
            (reason ? ` [${reason}]` : '')
        );
        this._settleRetryAttempts = 0;
        try {
          window.__freeGiftSyncPending = false;
        } catch (_) {}
        this._setShippingProductInteractionsBlocked(false);
        if (this._loaderShownViaShippingPage || this._loaderOwnedByFreeGift) {
          this._hideLoader({ force: true });
        }
        return;
      }
      log(
        `Deferring free-gift sync while multi-ship settles` +
          (reason ? ` — ${reason}` : '') +
          ` (retry #${this._settleRetryAttempts} in ${delay}ms)`
      );
      try {
        window.__freeGiftSyncPending = false;
      } catch (_) {}
      this._setShippingProductInteractionsBlocked(false);
      if (this._loaderShownViaShippingPage || this._loaderOwnedByFreeGift) {
        this._hideLoader({ force: true });
      }
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
      clearTimeout(this._settleRetryTimer);
      this._settleRetryTimer = setTimeout(() => {
        this._settleRetryTimer = null;
        void this.evaluateTierAndSync();
      }, delay);
    }

    /** B3 — idempotent tier reconcile */
    async evaluateTierAndSync(options) {
      if (!isFeatureEnabled()) return;

      if (this._syncInFlight) {
        this._syncQueued = true;
        return this._syncInFlight;
      }

      if (Date.now() < this.cooldownUntil) {
        if (this._isOnShippingPage() && !window.__freeGiftSyncPending && !this._syncQueued) {
          return;
        }
        this.cooldownUntil = 0;
      }

      if (isMultishipCartSettling()) {
        this._scheduleSettleRetry(2200, 'evaluateTierAndSync');
        return;
      }

      if (isMultiPackPricePreviewActive()) {
        this._scheduleSettleRetry(600, 'multi-pack price preview');
        return;
      }

      this._settleRetryAttempts = 0;
      this._syncQueued = false;
      this._syncInFlight = this._evaluateTierAndSyncInternal(options);
      try {
        await this._syncInFlight;
      } finally {
        this._syncInFlight = null;
        if (this._syncQueued) {
          this._syncQueued = false;
          this.cooldownUntil = 0;
          void this.evaluateTierAndSync();
        }
      }
    }

    /**
     * True when cart gift lines do not match eligibility (add/remove required).
     * Heal/property-only work is treated as silent — no shipping overlay.
     */
    _giftSyncNeedsVisibleMutation(cart, eligible) {
      if (!cart || !Array.isArray(cart.items)) return false;
      const has1 = giftExistsInCart('1', cart);
      const has2 = giftExistsInCart('2', cart);
      const visible1 = shippingPageHasVisibleFreeGiftRow('1');
      const visible2 = shippingPageHasVisibleFreeGiftRow('2');
      if (eligible === 'tier2') {
        if (has1 || visible1) return true;
        if (has2) {
          try {
            return collectDuplicateTierGiftsToRemove(cart, '2').length > 0;
          } catch (_) {
            return false;
          }
        }
        // Markers/SKU may already be present (CT lag) — treat as silent heal path.
        if (cartHasLikelyExistingTierGift(cart, '2') || visible2) return false;
        return true;
      }
      if (eligible === 'tier1') {
        if (has2 || visible2) return true;
        if (has1) {
          try {
            return collectDuplicateTierGiftsToRemove(cart, '1').length > 0;
          } catch (_) {
            return false;
          }
        }
        if (cartHasLikelyExistingTierGift(cart, '1') || visible1) return false;
        return true;
      }
      // eligible === 'none' — must remove any gift still painted or present in cart.
      return !!(
        has1 ||
        has2 ||
        visible1 ||
        visible2 ||
        cartHasLikelyExistingTierGift(cart, '1') ||
        cartHasLikelyExistingTierGift(cart, '2')
      );
    }

    async _evaluateTierAndSyncInternal(options) {
      const opts = options && typeof options === 'object' ? options : {};
      const forceLoader = opts.forceLoader === true;
      this.processing = true;
      let mutated = false;
      let ownedLoader = false;
      let mutationArmed = false;

      const armMutationUi = () => {
        if (mutationArmed) return;
        mutationArmed = true;
        try {
          window.__freeGiftMutationInProgress = true;
          window.__freeGiftSyncPending = true;
        } catch (_) {}
        try {
          document.dispatchEvent(new CustomEvent('free-gift:sync-start'));
        } catch (_) {}
        this._setExpressCheckoutGiftBusy(true);
        if (this._isOnShippingPage()) {
          this._showLoader('Updating your free gift…');
          ownedLoader = !!(this._loaderShownViaShippingPage || this._loaderOwnedByFreeGift);
        }
      };

      try {
        captureTier2CodeFromLanding();

        // Assessment phase is silent unless the caller forced a loader (Tier 2 code change)
        // or qty-chain already owns the overlay (message-only update below).
        if (forceLoader) {
          armMutationUi();
        } else if (this._isOnShippingPage()) {
          const loaderEl = document.getElementById('shipping-page-loader');
          const loaderVisible =
            !!(loaderEl && loaderEl.style.display !== 'none');
          // Qty / Save already showing overlay — update message only, do not re-arm pending
          // until we know a gift mutation is required.
          if (loaderVisible) {
            const msgEl = loaderEl.querySelector('.shipping-page-loader-message');
            if (msgEl && /quantity|shipping|cart|saving|updating/i.test(msgEl.textContent || '')) {
              // leave existing flow message unless we mutate
            }
          }
        }

        if (this._isOnShippingPage()) {
          // Page-load race: free-gift can run before MultiShip injects cartItemComponents.
          // Without it we fall back to cart.js totals that may include shipping → false Tier 1,
          // AND giftExistsInCart can miss a CT gift+shipping bundle → false re-add without shipping.
          try {
            const map = window.cartItemComponents;
            if (!map || typeof map !== 'object' || !Object.keys(map).length) {
              await refreshCartItemComponents();
            }
          } catch (_) {}
          try {
            const map2 = window.cartItemComponents;
            const ready = map2 && typeof map2 === 'object' && Object.keys(map2).length > 0;
            this._hydrationDeferAttempts = this._hydrationDeferAttempts || 0;
            if (!ready && this._hydrationDeferAttempts < 5) {
              this._hydrationDeferAttempts += 1;
              log(
                `cartItemComponents not ready — deferring sync to avoid false gift re-add (attempt ${this._hydrationDeferAttempts}/5)`
              );
              this._scheduleSettleRetry(1600, 'cartItemComponents not ready');
              return;
            }
            if (ready) this._hydrationDeferAttempts = 0;
          } catch (_) {}
        }

        let freshCart = await getFreshCart();
        if (!freshCart || !Array.isArray(freshCart.items)) {
          this._releaseShippingPageSyncUiIfIdle();
          return;
        }

        if (!freshCart.item_count) {
          try {
            sessionStorage.removeItem(TIER2_CODE_STORAGE);
            localStorage.removeItem(TIER2_CODE_STORAGE);
          } catch (_) {}
          this._releaseShippingPageSyncUiIfIdle();
          return;
        }

        /* Scenario 2: native WELCOME/ENJOY on cart → drop Tier 2 code so gift is removed
           and the Tier 2 Discount Function can stay off (enteredDiscountCodes present). */
        if (cartHasNativeShopifyPromo(freshCart) && (getAppliedTier2Code() || getPendingTier2Code())) {
          log('Native Shopify promo active — clearing Tier 2 theme code (one promo at a time)');
          clearAllTier2CodeState();
          notifyTier2CodeCleared();
        }

        if (isMultiPackPricePreviewActive()) {
          this._scheduleSettleRetry(600, 'multi-pack price preview (mid-sync)');
          return;
        }

        if (promotePendingTier2IfReady(freshCart)) {
          freshCart = await getFreshCart();
          if (!freshCart || !Array.isArray(freshCart.items) || !freshCart.item_count) {
            this._releaseShippingPageSyncUiIfIdle();
            return;
          }
        }

        let totalCents = calculateMainProductsTotal(freshCart);
        let eligible = determineEligibleTier(freshCart);
        log(`Effective total: $${(totalCents / 100).toFixed(2)} → eligible: ${eligible}`);

        const hadTier2Code = !!getAppliedTier2Code();
        let tier2DemotedThisPass = false;

        // Final-price demotion: progress bar / subtotal use final_line_price; lifted totals can
        // stay ≥ threshold on pre-discount line prices and leave CHERRYJAM + gift stuck applied.
        if (hadTier2Code && shouldDemoteAppliedTier2Code(freshCart)) {
          if (isMultishipCartSettling()) {
            log('Tier 2 final merchandise below threshold during settle — deferring demotion');
            this._scheduleSettleRetry(2500, 'tier2 final-price demotion recheck');
            return;
          }
          demoteAppliedTier2ToPending();
          tier2DemotedThisPass = true;
          totalCents = calculateMainProductsTotal(freshCart);
          eligible = determineEligibleTier(freshCart);
          log(
            `Demoted Tier 2 code — final merchandise $${(calculateMainProductsFinalTotal(freshCart) / 100).toFixed(2)} below threshold`
          );
        }

        // Tier 2 code still applied but cart momentarily under threshold (CT / save settle):
        // refresh once before clearing the code or removing the gift. False undercounts after
        // Save & Continue were wiping the Tier 2 gift while the code remained applied in UI storage.
        if (hadTier2Code && eligible !== 'tier2' && getAppliedTier2Code()) {
          if (isMultishipCartSettling()) {
            log('Tier 2 code applied but threshold miss during multi-ship settle — deferring sync');
            this._scheduleSettleRetry(2500, 'tier2 threshold recheck');
            return;
          }
          log('Tier 2 code applied but not eligible — re-checking cart total before clearing');
          if (this._isOnShippingPage()) await pause(900);
          freshCart = await getFreshCart();
          if (!freshCart || !Array.isArray(freshCart.items) || !freshCart.item_count) {
            this._releaseShippingPageSyncUiIfIdle();
            return;
          }
          totalCents = calculateMainProductsTotal(freshCart);
          eligible = determineEligibleTier(freshCart);
          log(
            `Re-check total: $${(totalCents / 100).toFixed(2)} → eligible: ${eligible}`
          );
          // CT lag can flip settle mid-recheck — defer again instead of clearing Tier 2.
          if (hadTier2Code && eligible !== 'tier2' && isMultishipCartSettling()) {
            log('Tier 2 still under threshold during settle after recheck — deferring');
            this._scheduleSettleRetry(2500, 'tier2 threshold recheck (post)');
            return;
          }
        }

        if (hadTier2Code && eligible !== 'tier2' && getAppliedTier2Code()) {
          demoteAppliedTier2ToPending();
          tier2DemotedThisPass = true;
          eligible = determineEligibleTier(freshCart);
          log('Demoted Tier 2 gift code — cart no longer meets Tier 2 requirements');
        }

        // False demotion during shipping settle: re-promote pending when cart still qualifies.
        if (!getAppliedTier2Code() && getPendingTier2Code() && meetsTier2PromotionThreshold(freshCart)) {
          if (promotePendingTier2IfReady(freshCart)) {
            freshCart = await getFreshCart();
            if (freshCart && Array.isArray(freshCart.items) && freshCart.item_count) {
              totalCents = calculateMainProductsTotal(freshCart);
              eligible = determineEligibleTier(freshCart);
              tier2DemotedThisPass = false;
              log('Re-promoted pending Tier 2 — cart still meets threshold');
            }
          }
        }

        // Shipping page: Gift Summary below threshold — remove stray gifts only when DOM is hydrated.
        // Address-assign / section swap can briefly show Product Total $0.00 while cart still qualifies.
        if (this._isOnShippingPage()) {
          const tier1Min = Math.round((parseFloat(getSettings().threshold) || 125) * 100);
          const dom = getDOMTotalCents();
          if (dom !== null && dom < tier1Min) {
            const merchOnly = cartMerchandiseOnlyHintCents(freshCart);
            const underAgreed =
              isShippingDomMerchandiseReliable(dom, freshCart) ||
              (dom > 0 &&
                (merchOnly <= 0 ||
                  merchOnly < tier1Min ||
                  Math.abs(dom - merchOnly) <= Math.max(Math.round(tier1Min * 0.15), 2500)));
            if (!underAgreed) {
              log(
                `Shipping page DOM $${(dom / 100).toFixed(2)} unreliable vs cart merchandise — skip force gift removal`
              );
              if (isMultishipCartSettling()) {
                this._scheduleSettleRetry(2000, 'shipping DOM merchandise hydrate');
                return;
              }
            } else {
              const hasGift =
                giftExistsInCart('1', freshCart) ||
                giftExistsInCart('2', freshCart) ||
                cartHasLikelyExistingTierGift(freshCart, '1') ||
                cartHasLikelyExistingTierGift(freshCart, '2') ||
                shippingPageHasVisibleFreeGiftRow('1') ||
                shippingPageHasVisibleFreeGiftRow('2');
              if (hasGift) {
                eligible = 'none';
                if (getAppliedTier2Code()) {
                  demoteAppliedTier2ToPending();
                  tier2DemotedThisPass = true;
                }
                log(
                  `Shipping page DOM $${(dom / 100).toFixed(2)} below $${(tier1Min / 100).toFixed(2)} — forcing gift removal`
                );
              }
            }
          }
        }

        if (tier2DemotedThisPass) {
          notifyTier2CodeCleared();
        }
        // Do NOT dispatch free-gift:tier2-code-changed here when the code was already applied.
        // That event arms syncGiftForced (force loader) and re-entered on every silent
        // "Gift already correct" pass — flashing "Updating your free gift…" after address assign.

        const keepTier = eligible === 'tier1' ? '1' : eligible === 'tier2' ? '2' : null;

        // Silent no-op: gift already matches eligibility — skip overlay on refresh / cart:updated / Save.
        if (
          !forceLoader &&
          !this._giftSyncNeedsVisibleMutation(freshCart, eligible)
        ) {
          // Still run light heal silently (no overlay) when gift already present.
          let silentHeal = false;
          try {
            if (eligible === 'tier2' || eligible === 'tier1') {
              silentHeal = !!(await healOrphanGiftShippingSafe());
              if (eligible === 'tier2') {
                const cartForProps = await getFreshCart();
                if (await ensureTier2GiftPropertiesOnCart(cartForProps)) silentHeal = true;
              }
            }
          } catch (_) {}
          if (silentHeal) {
            log('Gift already correct — silent heal/props only (no loader)');
          } else {
            log('Gift already correct — skipping loader / no cart mutation');
          }
          return;
        }

        armMutationUi();

        let cartForRemoval = freshCart;

        // Recipient context is captured only for diagnostics / cleanup of old gift shipping.
        // System auto-add of the *new* tier gift is ALWAYS standalone (new gift_ GID) —
        // never merge into the old gift's bundle / paid GID (customer chooses address).
        let giftUpgradeCtx = null;
        if (eligible === 'tier2' && giftExistsInCart('1', cartForRemoval)) {
          giftUpgradeCtx = collectGiftRecipientUpgradeContext(cartForRemoval, '1');
        } else if (eligible === 'tier1' && giftExistsInCart('2', cartForRemoval)) {
          giftUpgradeCtx = collectGiftRecipientUpgradeContext(cartForRemoval, '2');
        }

        if (eligible !== 'tier2') {
          log('Tier 2 inactive — Part J removal (component vs gift-only parent)');
          const r2 = await removeGiftsRespectingBundleShape(
            (id, qty) => this._changeQty(id, qty),
            cartForRemoval,
            '2'
          );
          if (r2.mutated) mutated = true;
          cartForRemoval = r2.cart || cartForRemoval;
          if (giftExistsInCart('2', cartForRemoval)) {
            console.warn(
              '[FreeGift] Tier 2 gift still present after Part J removal — check Cart Transform properties'
            );
          }
        }

        if (eligible !== 'tier1') {
          log('Tier 1 inactive — Part J removal (component vs gift-only parent)');
          const r1 = await removeGiftsRespectingBundleShape(
            (id, qty) => this._changeQty(id, qty),
            cartForRemoval,
            '1'
          );
          if (r1.mutated) mutated = true;
          cartForRemoval = r1.cart || cartForRemoval;
        }

        if (keepTier) {
          const dupes = collectDuplicateTierGiftsToRemove(await getFreshCart(), keepTier);
          for (const entry of dupes) {
            const id = entry.lineKey || entry.key;
            if (!id) continue;
            log(`Removing duplicate gift line id=${id}`);
            const ok = await this._changeQty(id, 0);
            if (ok) mutated = true;
            await pauseGiftCart(280);
          }
        }

        const cartAfterCleanup = await getFreshCart();

        if (eligible === 'tier2') {
          if (!giftExistsInCart('2', cartAfterCleanup)) {
            // Block false re-add on refresh when gift SKU is already present (CT prop lag)
            // or when orphan gift shipping still holds the saved recipient.
            if (cartHasLikelyExistingTierGift(cartAfterCleanup, '2')) {
              log(
                'Skip Tier 2 add — gift already present (markers/SKU lag); healing shipping if needed'
              );
              if (await healOrphanGiftShippingSafe()) mutated = true;
            } else {
              let reclaimGid = findOrphanGiftShippingGidSafe(cartAfterCleanup);
              if (reclaimGid) {
                log(`Adding Tier 2 gift reclaiming orphan shipping gid=${reclaimGid}`);
                const added = await this._addGift('2', {
                  preserveGid: reclaimGid,
                  forceStandalone: false,
                });
                if (added) mutated = true;
                if (await healOrphanGiftShippingSafe()) mutated = true;
              } else {
                log(
                  giftUpgradeCtx
                    ? 'Adding Tier 2 gift as NEW standalone bundle (replacing Tier 1 without merging)'
                    : 'Adding Tier 2 gift as standalone bundle'
                );
                const added = await this._addGift('2', {
                  preserveGid: null,
                  forceStandalone: true,
                });
                if (added) mutated = true;
              }
            }
          } else {
            log('Tier 2 gift already present — ensure properties / heal shipping');
            if (await healOrphanGiftShippingSafe()) mutated = true;
          }
          const cartForProps = await getFreshCart();
          const propsRepaired = await ensureTier2GiftPropertiesOnCart(cartForProps);
          if (propsRepaired) mutated = true;
        } else if (eligible === 'tier1') {
          if (!giftExistsInCart('1', cartAfterCleanup)) {
            if (cartHasLikelyExistingTierGift(cartAfterCleanup, '1')) {
              log(
                'Skip Tier 1 add — gift already present (markers/SKU lag); healing shipping if needed'
              );
              if (await healOrphanGiftShippingSafe()) mutated = true;
            } else {
              let reclaimGid = findOrphanGiftShippingGidSafe(cartAfterCleanup);
              const fromTier2Fallback =
                !!(giftUpgradeCtx && String(giftUpgradeCtx.fromTier) === '2');
              if (reclaimGid) {
                log(`Adding Tier 1 gift reclaiming orphan shipping gid=${reclaimGid}`);
                const added = await this._addGift('1', {
                  preserveGid: reclaimGid,
                  forceStandalone: false,
                });
                if (added) mutated = true;
                if (await healOrphanGiftShippingSafe()) mutated = true;
              } else {
                log(
                  fromTier2Fallback
                    ? 'Adding Tier 1 gift as NEW standalone bundle (Tier2→Tier1 fallback)'
                    : 'Adding Tier 1 gift as standalone bundle'
                );
                const added = await this._addGift('1', {
                  preserveGid: null,
                  forceStandalone: true,
                });
                if (added) mutated = true;
              }
            }
          } else {
            log('Tier 1 gift already present — heal shipping if split');
            if (await healOrphanGiftShippingSafe()) mutated = true;
          }
        } else {
          log('No gift tier eligible');
        }

        if (hadTier2Code && eligible !== 'tier2' && !mutated) {
          this._refreshShippingPage();
        }

        if (mutated) {
          window.__multishipTierUpdateHoldUntil = Date.now() + (this._isOnShippingPage() ? 12000 : 1500);
          if (this._isOnShippingPage()) {
            await refreshCartItemComponents();
          }
          // Orphan shipping cleanup is deferred — do NOT run it immediately after gift
          // add/swap. Cleaning while Cart Transform parents are opaque wiped paid products.
          if (!this._appliedDrawerFromApi) {
            await this._refreshCartDrawer();
          }
          this._appliedDrawerFromApi = false;
          this._publishCartUpdate();
          this._refreshShippingPage();
        }
      } catch (err) {
        console.error('[FreeGift] evaluateTierAndSync error:', err);
      } finally {
        try {
          window.__freeGiftMutationInProgress = false;
          window.__freeGiftSyncPending = false;
        } catch (_) {}
        this.processing = false;
        // PRE-CHECKOUT-WAIT-SHIPPING-2026-09-15: hold Continue-to-Payment across the post-sync
        // cart:updated → scheduleTierUpdate gap (and gift shipping re-add) after flags clear.
        try {
          if (
            this._isOnShippingPage() &&
            typeof window.multishipArmShippingSettleHold === 'function'
          ) {
            window.multishipArmShippingSettleHold(mutated ? 3000 : 1500);
          }
        } catch (_) {}
        const onShipping = this._isOnShippingPage();
        this.cooldownUntil = Date.now() + (onShipping ? (mutated ? 3000 : 1000) : 0);
        this._setShippingProductInteractionsBlocked(false);
        if (ownedLoader) this._hideLoader();
        this._setExpressCheckoutGiftBusy(false);
        this._releaseShippingPageSyncUiIfIdle();
        try {
          if (this._isOnShippingPage() && typeof window.multishipLockFreeGiftQuantityControls === 'function') {
            window.multishipLockFreeGiftQuantityControls(document.querySelector('shipping-page'));
          }
        } catch (_) {}
        try {
          document.dispatchEvent(
            new CustomEvent('free-gift:sync-idle', { detail: { mutated: !!mutated } })
          );
        } catch (_) {}
      }
    }

    async _addGift(tier, options) {
      const opts = options && typeof options === 'object' ? options : {};
      const s = getSettings();
      const isTier2 = tier === '2';
      const pg = s.promoGift;
      let variantId = isTier2 ? pg && pg.variantId : s.variantId;
      const productHandle = isTier2 ? pg && pg.productHandle : s.productHandle;

      if (!variantId && productHandle) {
        variantId = await this._fetchFirstVariantId(productHandle);
      }
      if (!variantId) {
        log('No variant ID for tier', tier);
        return false;
      }

      if (isTier2 && !getAppliedTier2Code()) {
        log('Cannot add Tier 2 gift — no applied code');
        return false;
      }

      // Final guard against page-refresh / race duplicates: never cart/add a second gift when
      // one already exists (including gift_ GID after Cart Transform stripped identity props).
      try {
        const guardCart = await getFreshCart();
        if (guardCart && giftExistsInCart(String(tier), guardCart)) {
          log(`Skip add tier ${tier} gift — already present (pre-add guard)`);
          if (isTier2) {
            await ensureTier2GiftPropertiesOnCart(guardCart);
          }
          await healOrphanGiftShippingSafe();
          return false;
        }
        if (guardCart && cartHasLikelyExistingTierGift(guardCart, String(tier))) {
          log(
            `Skip add tier ${tier} gift — configured gift already in cart (lag guard); healing shipping`
          );
          await healOrphanGiftShippingSafe();
          return false;
        }
      } catch (_) {}

      // Explicit preserveGid always wins (orphan gift_ shipping reclaim after Save/checkout).
      // forceStandalone only means "don't inherit a paid/Tier-upgrade GID" — callers pass
      // preserveGid when reclaiming orphan shipping under the same gift_ recipient id.
      let preserveGid =
        opts.preserveGid != null && String(opts.preserveGid).trim() !== ''
          ? String(opts.preserveGid).trim()
          : null;

      if (!preserveGid && this._isOnShippingPage()) {
        try {
          const reclaimCart = await getFreshCart();
          const findOrphan =
            typeof window.multishipFindOrphanGiftShippingGidToReclaim === 'function'
              ? window.multishipFindOrphanGiftShippingGidToReclaim
              : null;
          if (findOrphan && reclaimCart && Array.isArray(reclaimCart.items)) {
            const orphanGid = findOrphan(reclaimCart.items);
            if (orphanGid && String(orphanGid).startsWith('gift_')) {
              preserveGid = String(orphanGid);
              log(
                `Reclaiming orphan gift shipping gid=${preserveGid} for new tier ${tier} gift`
              );
            }
          }
        } catch (_) {}
      }

      const propOverrides = preserveGid ? { _gid: preserveGid } : {};
      const props = isTier2
        ? buildTier2GiftLineProperties(propOverrides)
        : buildTier1GiftLineProperties(propOverrides);

      if (preserveGid) {
        log(`Adding tier ${tier} gift reusing recipient gid=${preserveGid}`);
      } else {
        log(`Adding tier ${tier} gift as standalone gid=${props._gid}`);
      }
      log(isTier2 ? 'Adding Tier 2 gift with props' : 'Adding Tier 1 gift with props', props);

      try {
        const addBody = {
          items: [{ id: variantId, quantity: 1, properties: props }],
        };
        if (!this._isOnShippingPage()) {
          Object.assign(addBody, getGiftCartSectionPayload());
        }
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify(addBody),
        });
        if (!res.ok) {
          console.error('[FreeGift] cart/add.js failed', res.status);
          return false;
        }
        let parsed = null;
        try {
          parsed = await res.json();
        } catch (_) {}
        if (!this._isOnShippingPage() && parsed && applyGiftCartSections(parsed)) {
          this._appliedDrawerFromApi = true;
        }
      } catch (err) {
        console.error('[FreeGift] cart/add.js error', err);
        return false;
      }

      await pauseGiftCart(550);

      window.__freeGiftJustAdded = true;
      setTimeout(() => {
        window.__freeGiftJustAdded = false;
      }, 20000);
      return true;
    }

    async _fetchFirstVariantId(handle) {
      try {
        const res = await fetch(`/products/${handle}.js`);
        if (!res.ok) return null;
        const product = await res.json();
        const available = product.variants.find((v) => v.available);
        return available ? available.id : product.variants[0]?.id || null;
      } catch (_) {
        return null;
      }
    }

    async _changeQty(id, qty) {
      if (!id) return false;
      const onShipping = this._isOnShippingPage();
      const sectionPayload = onShipping ? null : getGiftCartSectionPayload();
      try {
        const changeBody = { id, quantity: qty };
        if (sectionPayload) Object.assign(changeBody, sectionPayload);
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify(changeBody),
        });
        if (res.ok) {
          if (!onShipping) {
            let parsed = null;
            try {
              parsed = await res.json();
            } catch (_) {}
            if (parsed && applyGiftCartSections(parsed)) {
              this._appliedDrawerFromApi = true;
            }
          }
          return true;
        }

        // Fallback: cart/update.js by line key (needed for some Cart Transform component keys)
        if (qty === 0) {
          const updates = {};
          updates[String(id)] = 0;
          const updateBody = { updates };
          if (sectionPayload) Object.assign(updateBody, sectionPayload);
          const upd = await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify(updateBody),
          });
          if (upd.ok && !onShipping) {
            let parsed = null;
            try {
              parsed = await upd.json();
            } catch (_) {}
            if (parsed && applyGiftCartSections(parsed)) {
              this._appliedDrawerFromApi = true;
            }
          }
          return upd.ok;
        }
        return false;
      } catch (_) {
        return false;
      }
    }

    _retryAfterDelay(ms) {
      clearTimeout(this._settleRetryTimer);
      this._settleRetryTimer = null;
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.debounceTimer = null;
        void this.evaluateTierAndSync();
      }, ms);
    }

    async _refreshCartDrawer() {
      try {
        const cartDrawer = document.querySelector('cart-drawer');
        if (!cartDrawer || typeof cartDrawer.renderContents !== 'function') return;
        const res = await fetch('/?sections=cart-drawer,cart-icon-bubble&_=' + Date.now(), {
          credentials: 'same-origin',
        });
        if (!res.ok) return;
        const sections = await res.json();
        cartDrawer.renderContents({ sections }, { openDrawer: false });
        if (sections['cart-icon-bubble'] && typeof window.syncCartIconBubble === 'function') {
          window.syncCartIconBubble(sections['cart-icon-bubble']);
        }
      } catch (_) {}
    }

    _publishCartUpdate() {
      if (typeof publish !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'free-gift' });
      }
      document.dispatchEvent(
        new CustomEvent('cart:updated', { bubbles: true, detail: { source: 'free-gift' } })
      );
    }

    _refreshShippingPage() {
      const sp = document.querySelector('shipping-page');
      if (!sp || typeof sp.refreshSectionFromServer !== 'function') return;
      clearTimeout(this._shippingSectionRefreshTimer);
      this._shippingSectionRefreshTimer = setTimeout(() => {
        this._shippingSectionRefreshTimer = null;
        const done = () => {
          try {
            if (typeof window.multishipLockFreeGiftQuantityControls === 'function') {
              window.multishipLockFreeGiftQuantityControls(sp);
            } else if (typeof multishipLockFreeGiftQuantityControls === 'function') {
              multishipLockFreeGiftQuantityControls(sp);
            }
          } catch (_) {}
        };
        Promise.resolve(sp.refreshSectionFromServer())
          .then(done)
          .catch(() => {
            done();
          });
      }, 400);
    }

    _setExpressCheckoutGiftBusy(busy) {
      window.__freeGiftCartBusy = !!busy;
    }

    _setShippingProductInteractionsBlocked(blocked) {
      const sp = document.querySelector('shipping-page');
      if (!sp) return;
      try {
        sp.querySelectorAll('multiship-product').forEach((row) => {
          row
            .querySelectorAll(
              '.quantity__input, .quantity__button, .multiship-product__split, .multiship-product__remove, ' +
                '.shipping-trigger__button, modal-opener button, .multiship-address-selector select, ' +
                '#apply-same-address-to-all'
            )
            .forEach((el) => {
              if (blocked) {
                el.setAttribute('disabled', 'disabled');
                el.setAttribute('aria-disabled', 'true');
              } else {
                el.removeAttribute('disabled');
                el.removeAttribute('aria-disabled');
              }
            });
        });
      } catch (_) {}
    }

    _showLoader(message) {
      const text = message != null ? String(message) : 'Updating your free gift…';
      this._loaderShownViaShippingPage = false;
      const sp = document.querySelector('shipping-page');
      const loaderEl = document.getElementById('shipping-page-loader');
      const loaderVisible = !!(loaderEl && loaderEl.style.display !== 'none');
      if (sp && typeof sp.showShippingPageLoader === 'function') {
        if (loaderVisible) {
          const msgEl = loaderEl.querySelector('.shipping-page-loader-message');
          if (msgEl) msgEl.textContent = text;
          this._setShippingProductInteractionsBlocked(true);
          return;
        }
        sp.showShippingPageLoader(text);
        this._loaderShownViaShippingPage = true;
        this._setShippingProductInteractionsBlocked(true);
        return;
      }
      if (loaderEl) {
        loaderEl.style.display = 'flex';
        const m = loaderEl.querySelector('.shipping-page-loader-message');
        if (m) m.textContent = text;
        document.body.classList.add('overflow-hidden');
        this._loaderOwnedByFreeGift = true;
        this._setShippingProductInteractionsBlocked(true);
      }
    }

    _hideLoader(options) {
      const force = !!(options && options.force);
      this._setShippingProductInteractionsBlocked(false);
      if (this._loaderShownViaShippingPage) {
        const sp = document.querySelector('shipping-page');
        if (sp && typeof sp.hideShippingPageLoader === 'function') {
          sp.hideShippingPageLoader(force ? { force: true } : undefined);
        }
        this._loaderShownViaShippingPage = false;
        return;
      }
      if (this._loaderOwnedByFreeGift) {
        const loader = document.getElementById('shipping-page-loader');
        if (loader) loader.style.display = 'none';
        this._loaderOwnedByFreeGift = false;
        try {
          document.body.classList.remove('overflow-hidden');
        } catch (_) {}
      }
    }

    notifyMultishipQtySettled(options) {
      const opts = options && typeof options === 'object' ? options : {};
      const deferMs = opts.deferMs != null ? Number(opts.deferMs) : opts.chainFromMultiship ? 0 : 450;

      const run = async () => {
        if (window.__multishipQtyChangeInFlight && !opts.chainFromMultiship) {
          await pause(150);
          return this.notifyMultishipQtySettled(options);
        }
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
        this.cooldownUntil = 0;
        try {
          // Qty chain already owns the shipping overlay — only update the message if visible.
          // Do not arm __freeGiftSyncPending until evaluateTierAndSync knows a mutation is needed
          // (otherwise Save/qty hide is blocked while gift quietly no-ops).
          const loaderEl = document.getElementById('shipping-page-loader');
          const loaderVisible =
            !!(loaderEl && loaderEl.style.display !== 'none');
          if (loaderVisible) {
            const msgEl = loaderEl.querySelector('.shipping-page-loader-message');
            if (msgEl) msgEl.textContent = 'Updating your free gift…';
          }
          log('notifyMultishipQtySettled → evaluateTierAndSync');
          await this.evaluateTierAndSync();
        } finally {
          try {
            window.__freeGiftSyncPending = false;
          } catch (_) {}
          try {
            if (typeof window.multishipArmShippingSettleHold === 'function') {
              window.multishipArmShippingSettleHold();
            }
          } catch (_) {}
          this._setShippingProductInteractionsBlocked(false);
          // evaluateTierAndSync may have deferred without clearing ownership flags
          if (
            !this.processing &&
            !this.debounceTimer &&
            !this._settleRetryTimer &&
            (this._loaderShownViaShippingPage || this._loaderOwnedByFreeGift)
          ) {
            this._hideLoader();
          }
        }
      };

      if (deferMs > 0) {
        return new Promise((resolve) => {
          setTimeout(() => {
            run().then(resolve).catch(() => resolve());
          }, deferMs);
        });
      }
      return run();
    }
  }

  window.FreeGiftCore = {
    getFreshCart,
    giftExistsInCart,
    cartHasLikelyExistingTierGift,
    isTier2GiftLine,
    isPaidMainProductLine,
    collectTier2GiftRemovalPlan,
    removeGiftsRespectingBundleShape,
    isGiftMarkedLine,
    meetsTier2Threshold,
    getTier2ThresholdCents,
    calculateMainProductsTotal,
    determineEligibleTier,
    evaluateTierAndSync: () => {
      if (window.freeGiftManager && typeof window.freeGiftManager.evaluateTierAndSync === 'function') {
        return window.freeGiftManager.evaluateTierAndSync();
      }
      return Promise.resolve();
    },
    isTier2ThemeCode,
    matchesPromoGiftCodeSetting,
    getAppliedTier2Code,
    setAppliedTier2Code,
    clearAppliedTier2Code,
    demoteAppliedTier2ToPending,
    calculateMainProductsFinalTotal,
    meetsTier2FinalMerchandiseThreshold,
    meetsTier2PromotionThreshold,
    meetsGiftThresholdForTier,
    shouldDemoteAppliedTier2Code,
    getPendingTier2Code,
    setPendingTier2Code,
    clearPendingTier2Code,
    captureTier2CodeFromLanding,
    promotePendingTier2IfReady,
    clearAllTier2CodeState,
    cartHasNativeShopifyPromo,
    buildTier1GiftLineProperties,
    buildTier2GiftLineProperties,
    ensureTier2GiftPropertiesOnCart,
    getTier2CatalogPriceCents,
    isFeatureEnabled,
    parseTier2Codes,
    TIER2_CODE_STORAGE,
    TIER2_PENDING_STORAGE,
  };

  function boot() {
    if (window.freeGiftManager) return;
    if (!isFeatureEnabled()) return;
    if (typeof subscribe === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') {
      setTimeout(boot, 100);
      return;
    }
    new FreeGiftManager();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
